/**
 * Elite Batch 20 Part 2 — Workflow Templates 1868–1887
 * Procurement thread builder, product launch advocacy campaign, product launch
 * media outreach, product update interest cohort, product usage milestone
 * celebration, product-qualified event outbound, promotion trigger outreach,
 * quarterly account review prep, RFP contact expansion, ROI personalization
 * engine, Reddit brand mention monitor, Reddit competitor mention monitor,
 * referral partner activation, renewal stakeholder mapping, repeat pricing page
 * visitor alert, research collaboration outreach, resource page link outreach,
 * retail seasonal promotions outreach, review champion campaign builder, and
 * review site alert to sales workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 20 PART 2 — TEMPLATES 1868–1887
// =============================================================================

export const ELITE_BATCH20_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1868. PROCUREMENT THREAD BUILDER (9 nodes)
  // ===================================================================

  "Procurement Thread Builder": {
    summary:
      "Fires when a procurement stage signal arrives via webhook, extracts deal context with AI, fetches account data from HubSpot, identifies procurement stakeholders via API, enriches stakeholder profiles via Clearbit, assembles a vendor evaluation package with runCode, generates a multi-stakeholder engagement plan with AI, branches by procurement complexity, sends initial outreach emails, and creates a procurement tracker in HubSpot.",
    nodes: [
      trigger("Webhook: Procurement stage signal", "webhook_app", {
        source: "crm_pipeline",
        event: "deal.procurement_stage_entered",
        howItStarts:
          "Fires when a deal enters the procurement stage in the CRM — signals that the buyer has moved from evaluation to formal vendor assessment with procurement team involvement",
        setup:
          "Configure CRM webhook for procurement stage entry. Set OpenAI API key for stakeholder engagement planning. Set HubSpot API token for account data and tracker creation. Set Clearbit API key for stakeholder enrichment. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract procurement data
      aiNode(
        "2",
        "AI Extract Procurement Deal Context",
        280,
        "You are a procurement intelligence parser. Extract the deal ID, deal name, company name, company domain, deal amount, deal owner email, procurement stage (rfp_issued, vendor_shortlist, evaluation, negotiation, final_review), the primary contact name and email, expected close date, product line, and any procurement notes from the raw webhook payload. Respond JSON: {deal_id: string, deal_name: string, company_name: string, company_domain: string, deal_amount: string, deal_owner_email: string, procurement_stage: string, primary_contact_name: string, primary_contact_email: string, expected_close: string, product_line: string, procurement_notes: string}",
        "Raw webhook payload: {webhook_payload}. Extract all procurement deal context regardless of field naming conventions.",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "deal_owner_email", as: "deal_owner_email", type: "string" },
          {
            field: "procurement_stage",
            as: "procurement_stage",
            type: "string",
          },
          {
            field: "primary_contact_name",
            as: "primary_contact_name",
            type: "string",
          },
          {
            field: "primary_contact_email",
            as: "primary_contact_email",
            type: "string",
          },
          { field: "expected_close", as: "expected_close", type: "string" },
          { field: "product_line", as: "product_line", type: "string" },
          {
            field: "procurement_notes",
            as: "procurement_notes",
            type: "string",
          },
        ],
      ),

      // 3. Fetch account deal context from HubSpot
      hubspotGetNode(
        "3",
        "Fetch Account Deal Context HubSpot",
        520,
        "crm/v3/objects/companies/search?query={company_name}",
        [
          { name: "results[0].id", customName: "company_id", type: "string" },
          {
            name: "results[0].properties.name",
            customName: "hs_company_name",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "company_industry",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "company_size",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "company_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 4. Identify procurement stakeholders via Hunter.io API
      n("4", "hitApi", "Identify Procurement Stakeholders", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={company_domain}&api_key={hunter_api_key}&department=management&limit=10",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "stakeholder_email_1",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "stakeholder_first_1",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "stakeholder_last_1",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "stakeholder_role_1",
              type: "string",
            },
            {
              name: "data.emails[1].value",
              customName: "stakeholder_email_2",
              type: "string",
            },
            {
              name: "data.emails[1].position",
              customName: "stakeholder_role_2",
              type: "string",
            },
            {
              name: "meta.results",
              customName: "total_stakeholders_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. Enrich stakeholder profiles via Clearbit
      n("5", "hitApi", "Enrich Stakeholder Profiles Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={stakeholder_email_1}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_full_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            { name: "role", customName: "enriched_role", type: "string" },
            {
              name: "seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "linkedin.handle",
              customName: "stakeholder_linkedin",
              type: "string",
            },
            {
              name: "employment.name",
              customName: "enriched_company",
              type: "string",
            },
          ],
        },
      }),

      // 6. Assemble vendor evaluation package runCode
      n("6", "runCode", "Assemble Vendor Evaluation Package", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a structured vendor evaluation package for the procurement team
const dealAmount = parseFloat(vars.deal_amount) || 0;
const stakeholderCount = parseInt(vars.total_stakeholders_found) || 1;
const stage = (vars.procurement_stage || "evaluation").toLowerCase();
const complexity = stakeholderCount > 5 ? "high" : stakeholderCount > 2 ? "medium" : "low";
const packageComponents = ["Executive Summary", "Product Overview", "Technical Architecture"];
if (dealAmount > 100000) packageComponents.push("Enterprise SLA Terms", "Custom Implementation Plan");
if (dealAmount > 50000) packageComponents.push("ROI Analysis", "Customer References");
packageComponents.push("Pricing Proposal", "Security & Compliance Documentation");
if (stage === "rfp_issued") packageComponents.push("RFP Response Document");
const threadCount = Math.min(stakeholderCount, 4);
const stakeholderBriefing = "Primary: " + (vars.enriched_full_name || vars.stakeholder_first_1 + " " + vars.stakeholder_last_1) + " (" + (vars.enriched_title || vars.stakeholder_role_1) + ") | Secondary: " + (vars.stakeholder_role_2 || "TBD");
return {
  procurement_complexity: complexity,
  package_components: packageComponents.join(", "),
  component_count: packageComponents.length,
  thread_count: threadCount,
  stakeholder_briefing: stakeholderBriefing,
  deal_tier: dealAmount > 100000 ? "enterprise" : dealAmount > 25000 ? "mid_market" : "standard",
  recommended_approach: complexity === "high" ? "multi_thread_executive" : complexity === "medium" ? "dual_thread" : "single_thread"
};`,
          outputVariables: [
            { name: "procurement_complexity", type: "string" },
            { name: "package_components", type: "string" },
            { name: "component_count", type: "number" },
            { name: "thread_count", type: "number" },
            { name: "stakeholder_briefing", type: "string" },
            { name: "deal_tier", type: "string" },
            { name: "recommended_approach", type: "string" },
          ],
        },
      }),

      // 7. AI multi-stakeholder engagement plan
      aiNode(
        "7",
        "AI Multi-Stakeholder Engagement Plan",
        1480,
        "You are a strategic account executive specializing in complex procurement cycles. Given the deal context, stakeholder profiles, and procurement complexity, generate a multi-threaded engagement plan. For each stakeholder, specify the messaging angle, value proposition emphasis, and communication cadence. Respond JSON: {engagement_strategy: string, primary_email_subject: string, primary_email_body: string, secondary_outreach_subject: string, secondary_outreach_body: string, timeline_recommendation: string, key_risk_factors: string}",
        "Deal: {deal_name} (${deal_amount}), Company: {company_name} ({company_industry}, {company_size} employees). Procurement stage: {procurement_stage}. Complexity: {procurement_complexity}. Thread count: {thread_count}. Approach: {recommended_approach}. Package: {package_components}. Primary stakeholder: {enriched_full_name} ({enriched_title}, {enriched_seniority}). Secondary: {stakeholder_role_2}. Notes: {procurement_notes}. Expected close: {expected_close}. Build the engagement plan.",
        [
          {
            field: "engagement_strategy",
            as: "engagement_strategy",
            type: "string",
          },
          {
            field: "primary_email_subject",
            as: "primary_email_subject",
            type: "string",
          },
          {
            field: "primary_email_body",
            as: "primary_email_body",
            type: "string",
          },
          {
            field: "secondary_outreach_subject",
            as: "secondary_outreach_subject",
            type: "string",
          },
          {
            field: "secondary_outreach_body",
            as: "secondary_outreach_body",
            type: "string",
          },
          {
            field: "timeline_recommendation",
            as: "timeline_recommendation",
            type: "string",
          },
          { field: "key_risk_factors", as: "key_risk_factors", type: "string" },
        ],
      ),

      // 8. Branch by procurement complexity
      n("8", "condition", "High Procurement Complexity?", 1720, {
        conditionDetails: {
          conditions: [
            {
              variable: "procurement_complexity",
              operator: "eq",
              value: "high",
            },
          ],
        },
      }),

      // 9a. Send initial procurement emails (high complexity → also sends secondary thread)
      emailNode(
        "9",
        "Send Procurement Outreach Emails",
        1960,
        "{stakeholder_email_1}",
        "{primary_email_subject}",
        "{primary_email_body}<p>Included materials: {package_components}</p><p>Strategy: {engagement_strategy}</p><p>Timeline: {timeline_recommendation}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1869. PRODUCT LAUNCH ADVOCACY CAMPAIGN (10 nodes)
  // ===================================================================

  "Product Launch Advocacy Campaign": {
    summary:
      "Starts from a manual trigger with launch details, fetches the customer advocate list from HubSpot, scores advocate influence with runCode, drafts personalized advocacy asks with AI, branches by advocate tier, sends advocacy emails, posts social share reminders to Slack, monitors activity in Sheets, tracks advocacy completion in Sheets, and sends a thank-you reward email.",
    nodes: [
      trigger("Manual Trigger: Product launch advocacy", "manual", {
        howItStarts:
          "Triggered manually when the product marketing team is ready to activate customer advocates for a new product launch — includes launch details, advocate list criteria, and campaign goals",
        setup:
          "Configure HubSpot API token for advocate list retrieval. Set OpenAI API key for personalized advocacy drafting. Configure email sending credentials. Set Slack webhook for social reminders. Set Google Sheets credentials for activity tracking.",
        inputFields: [
          {
            name: "launch_name",
            type: "string",
            example: "AI Workflow Builder v3.0",
          },
          { name: "launch_date", type: "string", example: "2026-04-15" },
          {
            name: "launch_url",
            type: "string",
            example: "https://company.com/launches/ai-workflow-v3",
          },
          {
            name: "key_features",
            type: "string",
            example:
              "AI-powered node suggestions, visual debugging, template marketplace",
          },
          { name: "social_hashtag", type: "string", example: "#AIWorkflow" },
          {
            name: "advocate_segment",
            type: "string",
            example: "power_users_90_days",
          },
        ],
      }),

      // 2. Fetch customer advocate list from HubSpot
      hubspotGetNode(
        "2",
        "Fetch Customer Advocate List HubSpot",
        280,
        "crm/v3/lists/{advocate_segment}/memberships",
        [
          {
            name: "results[0].id",
            customName: "advocate_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "advocate_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "advocate_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "advocate_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "advocate_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "advocate_title",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "advocate_page_views",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
          { name: "total", customName: "total_advocates", type: "number" },
        ],
      ),

      // 3. Score advocate influence with runCode
      n("3", "runCode", "Score Advocate Influence", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score advocate influence based on engagement, title, and activity
const pageViews = parseInt(vars.advocate_page_views) || 0;
const title = (vars.advocate_title || "").toLowerCase();
const lastContacted = vars.last_contacted ? new Date(vars.last_contacted) : null;
const daysSinceContact = lastContacted ? Math.floor((Date.now() - lastContacted.getTime()) / (1000 * 60 * 60 * 24)) : 999;
let influenceScore = 0;
if (pageViews > 100) influenceScore += 30;
else if (pageViews > 50) influenceScore += 20;
else if (pageViews > 10) influenceScore += 10;
if (title.includes("vp") || title.includes("director") || title.includes("head")) influenceScore += 30;
else if (title.includes("manager") || title.includes("lead")) influenceScore += 20;
else if (title.includes("senior")) influenceScore += 15;
else influenceScore += 5;
if (daysSinceContact < 30) influenceScore += 25;
else if (daysSinceContact < 90) influenceScore += 15;
else influenceScore += 5;
const advocateTier = influenceScore >= 70 ? "platinum" : influenceScore >= 45 ? "gold" : "silver";
const askType = advocateTier === "platinum" ? "case_study_and_social" : advocateTier === "gold" ? "social_and_review" : "review_only";
return {
  influence_score: influenceScore,
  advocate_tier: advocateTier,
  ask_type: askType,
  days_since_contact: daysSinceContact,
  engagement_level: pageViews > 50 ? "high" : pageViews > 10 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "influence_score", type: "number" },
            { name: "advocate_tier", type: "string" },
            { name: "ask_type", type: "string" },
            { name: "days_since_contact", type: "number" },
            { name: "engagement_level", type: "string" },
          ],
        },
      }),

      // 4. AI personalized advocacy asks
      aiNode(
        "4",
        "AI Personalized Advocacy Asks",
        760,
        "You are a customer advocacy specialist. Given a product launch, an advocate profile with their tier and influence score, and the type of ask appropriate for their tier, craft a personalized advocacy email that makes them feel valued and excited to participate. For platinum advocates, emphasize exclusivity and early access. For gold, emphasize community impact. For silver, keep it simple and rewarding. Respond JSON: {advocacy_subject: string, advocacy_body_html: string, social_post_suggestion: string, reward_description: string, cta_link: string}",
        "Launch: {launch_name} (Features: {key_features}). Launch date: {launch_date}. URL: {launch_url}. Hashtag: {social_hashtag}. Advocate: {advocate_first_name} {advocate_last_name} at {advocate_company} ({advocate_title}). Tier: {advocate_tier}. Influence: {influence_score}. Ask type: {ask_type}. Engagement: {engagement_level}. Craft the personalized advocacy ask.",
        [
          { field: "advocacy_subject", as: "advocacy_subject", type: "string" },
          {
            field: "advocacy_body_html",
            as: "advocacy_body_html",
            type: "string",
          },
          {
            field: "social_post_suggestion",
            as: "social_post_suggestion",
            type: "string",
          },
          {
            field: "reward_description",
            as: "reward_description",
            type: "string",
          },
          { field: "cta_link", as: "cta_link", type: "string" },
        ],
      ),

      // 5. Branch by advocate tier
      n("5", "condition", "Platinum or Gold Advocate?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "advocate_tier",
              operator: "in",
              value: "platinum,gold",
            },
          ],
        },
      }),

      // 6. Send advocacy emails
      emailNode(
        "6",
        "Send Advocacy Campaign Email",
        1240,
        "{advocate_email}",
        "{advocacy_subject}",
        "{advocacy_body_html}<p>Suggested social post: {social_post_suggestion}</p><p>Your reward: {reward_description}</p>",
      ),

      // 7. Post social share reminders to Slack
      slackNode(
        "7",
        "Post Social Share Reminder Slack",
        1480,
        "Advocacy Alert: *{advocate_first_name} {advocate_last_name}* ({advocate_tier} tier, score {influence_score}) has been invited to advocate for *{launch_name}*\\nAsk type: {ask_type} | Company: {advocate_company}\\nSuggested post: {social_post_suggestion}\\nHashtag: {social_hashtag}",
      ),

      // 8. Monitor activity in Sheets
      sheetsNode(
        "8",
        "Log Advocacy Activity Sheets",
        1720,
        "AdvocacyTracker",
        '[["{advocate_first_name} {advocate_last_name}","{advocate_email}","{advocate_company}","{advocate_tier}","{influence_score}","{ask_type}","{launch_name}","invited","pending","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),

      // 9. Track advocacy completion in Sheets
      sheetsNode(
        "9",
        "Track Advocacy Completion Sheets",
        1960,
        "AdvocacyResults",
        '[["{launch_name}","{advocate_tier}","{advocate_email}","{ask_type}","{engagement_level}","email_sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),

      // 10. Send thank-you reward email (platinum/gold path)
      emailNode(
        "10",
        "Send Thank-You Reward Email",
        1480,
        "{advocate_email}",
        "Thank you for championing {launch_name}, {advocate_first_name}!",
        "<p>Hi {advocate_first_name},</p><p>Your advocacy means the world to us. As a {advocate_tier} advocate, here is your exclusive reward: {reward_description}</p><p>We could not have launched {launch_name} without champions like you at {advocate_company}.</p>",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "10", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("10", "9"),
    ],
  },

  // ===================================================================
  // 1870. PRODUCT LAUNCH MEDIA OUTREACH (8 nodes)
  // ===================================================================

  "Product Launch Media Outreach": {
    summary:
      "Starts from a manual trigger with media campaign details, fetches the target media list with runCode, researches journalist coverage with AI, generates exclusive access offers with AI, validates journalist contacts via API, branches by exclusivity tier, sends media pitches via email, and logs outreach to a PR tracker in Sheets.",
    nodes: [
      trigger("Manual Trigger: Media outreach campaign", "manual", {
        howItStarts:
          "Triggered manually when the PR team is ready to launch media outreach for a product announcement — includes product details, embargo date, media list criteria, and exclusivity tiers",
        setup:
          "Configure OpenAI API key for journalist research and pitch generation. Set Hunter.io API key for contact validation. Configure email sending credentials. Set Google Sheets credentials for PR outreach tracking.",
        inputFields: [
          {
            name: "product_name",
            type: "string",
            example: "OmniEngage AI Suite",
          },
          {
            name: "announcement_type",
            type: "string",
            example: "product_launch",
          },
          {
            name: "embargo_date",
            type: "string",
            example: "2026-04-10T09:00:00Z",
          },
          {
            name: "key_talking_points",
            type: "string",
            example:
              "AI-powered customer engagement, 3x conversion improvement, enterprise-grade security",
          },
          {
            name: "media_tier",
            type: "string",
            example: "tier1_tech,tier2_business",
          },
          {
            name: "exclusivity_budget",
            type: "string",
            example: "2 exclusives, 5 early access",
          },
        ],
      }),

      // 2. Build target media list with runCode
      n("2", "runCode", "Build Target Media List", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse media tier configuration and build target list structure
const tiers = (vars.media_tier || "").split(",").map(t => t.trim());
const exclusivityBudget = vars.exclusivity_budget || "1 exclusive, 3 early access";
const exclusiveCount = parseInt((exclusivityBudget.match(/(\\d+)\\s*exclusive/i) || [0, 1])[1]);
const earlyAccessCount = parseInt((exclusivityBudget.match(/(\\d+)\\s*early/i) || [0, 3])[1]);
const tierLabels = {
  "tier1_tech": "Top-tier tech publications (TechCrunch, The Verge, Wired)",
  "tier2_business": "Business & industry media (Forbes Tech, Business Insider)",
  "tier3_vertical": "Vertical/niche publications (MarTech, SaaStr)"
};
const targetOutlets = tiers.map(t => tierLabels[t] || t).join("; ");
const campaignId = "MEDIA-" + Date.now().toString(36).toUpperCase();
return {
  target_outlets: targetOutlets,
  exclusive_slots: exclusiveCount,
  early_access_slots: earlyAccessCount,
  total_tiers: tiers.length,
  campaign_id: campaignId,
  tier_list: tiers.join(", "),
  outreach_strategy: exclusiveCount > 0 ? "exclusive_first_then_broad" : "simultaneous_broad"
};`,
          outputVariables: [
            { name: "target_outlets", type: "string" },
            { name: "exclusive_slots", type: "number" },
            { name: "early_access_slots", type: "number" },
            { name: "total_tiers", type: "number" },
            { name: "campaign_id", type: "string" },
            { name: "tier_list", type: "string" },
            { name: "outreach_strategy", type: "string" },
          ],
        },
      }),

      // 3. AI research journalist coverage
      aiNode(
        "3",
        "AI Research Journalist Coverage",
        520,
        "You are a PR research specialist. Given a product announcement and target media outlets, identify the most relevant journalist to pitch at each tier. Consider their recent coverage areas, beat alignment, and likely interest. Provide a journalist name, outlet, beat, and the angle that would resonate most. Respond JSON: {journalist_name: string, journalist_outlet: string, journalist_beat: string, coverage_angle: string, pitch_hook: string, journalist_twitter: string, exclusivity_tier: string}",
        "Product: {product_name}. Announcement: {announcement_type}. Talking points: {key_talking_points}. Target outlets: {target_outlets}. Tiers: {tier_list}. Strategy: {outreach_strategy}. Exclusive slots: {exclusive_slots}. Research the ideal journalist to pitch.",
        [
          { field: "journalist_name", as: "journalist_name", type: "string" },
          {
            field: "journalist_outlet",
            as: "journalist_outlet",
            type: "string",
          },
          { field: "journalist_beat", as: "journalist_beat", type: "string" },
          { field: "coverage_angle", as: "coverage_angle", type: "string" },
          { field: "pitch_hook", as: "pitch_hook", type: "string" },
          {
            field: "journalist_twitter",
            as: "journalist_twitter",
            type: "string",
          },
          { field: "exclusivity_tier", as: "exclusivity_tier", type: "string" },
        ],
      ),

      // 4. AI exclusive access offers
      aiNode(
        "4",
        "AI Generate Exclusive Access Offers",
        760,
        "You are an expert PR pitch writer. Given the journalist profile, their outlet, beat, and the product details, craft a compelling media pitch email. If the journalist is in the exclusive tier, emphasize the exclusive access opportunity and embargo terms. If early access, emphasize hands-on preview. For broad outreach, lead with the news hook. Respond JSON: {pitch_subject: string, pitch_body_html: string, embargo_terms: string, access_offer: string, follow_up_plan: string}",
        "Journalist: {journalist_name} at {journalist_outlet} (beat: {journalist_beat}). Product: {product_name}. Talking points: {key_talking_points}. Angle: {coverage_angle}. Hook: {pitch_hook}. Exclusivity tier: {exclusivity_tier}. Embargo date: {embargo_date}. Craft the perfect pitch.",
        [
          { field: "pitch_subject", as: "pitch_subject", type: "string" },
          { field: "pitch_body_html", as: "pitch_body_html", type: "string" },
          { field: "embargo_terms", as: "embargo_terms", type: "string" },
          { field: "access_offer", as: "access_offer", type: "string" },
          { field: "follow_up_plan", as: "follow_up_plan", type: "string" },
        ],
      ),

      // 5. Validate journalist contacts via Hunter.io
      n("5", "hitApi", "Validate Journalist Contact via Hunter", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-finder?domain={journalist_outlet}&first_name={journalist_name}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.email",
              customName: "journalist_email",
              type: "string",
            },
            {
              name: "data.confidence",
              customName: "email_confidence",
              type: "number",
            },
            {
              name: "data.sources[0].uri",
              customName: "email_source_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Branch by exclusivity tier
      n("6", "condition", "Exclusive Tier Journalist?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "exclusivity_tier",
              operator: "eq",
              value: "exclusive",
            },
          ],
        },
      }),

      // 7. Send media pitch email
      emailNode(
        "7",
        "Send Media Pitch Email",
        1480,
        "{journalist_email}",
        "{pitch_subject}",
        "{pitch_body_html}<p>{embargo_terms}</p><p>Access: {access_offer}</p>",
      ),

      // 8. Log outreach to PR tracker
      sheetsNode(
        "8",
        "Log Outreach PR Tracker Sheets",
        1720,
        "PROutreach",
        '[["{campaign_id}","{journalist_name}","{journalist_outlet}","{journalist_beat}","{journalist_email}","{exclusivity_tier}","{pitch_subject}","{email_confidence}","{access_offer}","sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1871. PRODUCT UPDATE INTEREST COHORT (8 nodes)
  // ===================================================================

  "Product Update Interest Cohort": {
    summary:
      "Fires when an update engagement signal arrives via webhook, extracts engagement data with AI, aggregates engagement signals with runCode, fetches customer account data from HubSpot, identifies expansion signals with AI, scores expansion likelihood with runCode, branches by expansion score, routes high-intent signals to CSM via Slack, and sends expansion nudge emails.",
    nodes: [
      trigger("Webhook: Product update engagement signal", "webhook_app", {
        source: "product_analytics",
        event: "user.update_engagement",
        howItStarts:
          "Fires when a customer shows strong engagement with a product update — repeated feature usage, deep documentation visits, or upgrade page exploration signals potential expansion interest",
        setup:
          "Configure product analytics webhook for update engagement signals. Set OpenAI API key for signal analysis. Set HubSpot API token for account data. Configure Slack webhook for CSM alerts. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract engagement data
      aiNode(
        "2",
        "AI Extract Update Engagement Data",
        280,
        "You are a product analytics parser. Extract the user email, user name, company name, account ID, the feature or update engaged with, engagement type (feature_usage, docs_visit, upgrade_page, changelog_click), engagement count, session duration minutes, the product plan they are on, and any additional context from the raw webhook payload. Respond JSON: {user_email: string, user_name: string, company_name: string, account_id: string, feature_engaged: string, engagement_type: string, engagement_count: string, session_minutes: string, current_plan: string, engagement_context: string}",
        "Raw webhook payload: {webhook_payload}. Extract all product update engagement data regardless of field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "feature_engaged", as: "feature_engaged", type: "string" },
          { field: "engagement_type", as: "engagement_type", type: "string" },
          { field: "engagement_count", as: "engagement_count", type: "string" },
          { field: "session_minutes", as: "session_minutes", type: "string" },
          { field: "current_plan", as: "current_plan", type: "string" },
          {
            field: "engagement_context",
            as: "engagement_context",
            type: "string",
          },
        ],
      ),

      // 3. Aggregate engagement signals runCode
      n("3", "runCode", "Aggregate Engagement Signals", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score and aggregate engagement signals to determine expansion potential
const engagementCount = parseInt(vars.engagement_count) || 1;
const sessionMinutes = parseInt(vars.session_minutes) || 0;
const engagementType = (vars.engagement_type || "").toLowerCase();
const currentPlan = (vars.current_plan || "").toLowerCase();
let signalStrength = 0;
if (engagementType === "upgrade_page") signalStrength += 40;
else if (engagementType === "feature_usage") signalStrength += 25;
else if (engagementType === "docs_visit") signalStrength += 15;
else signalStrength += 10;
if (engagementCount > 5) signalStrength += 25;
else if (engagementCount > 2) signalStrength += 15;
else signalStrength += 5;
if (sessionMinutes > 30) signalStrength += 20;
else if (sessionMinutes > 10) signalStrength += 10;
const canExpand = currentPlan !== "enterprise" && currentPlan !== "unlimited";
const signalCategory = signalStrength >= 60 ? "hot" : signalStrength >= 35 ? "warm" : "cool";
return {
  signal_strength: signalStrength,
  signal_category: signalCategory,
  can_expand: canExpand ? "true" : "false",
  engagement_summary: engagementType + " x" + engagementCount + " (" + sessionMinutes + "min)",
  plan_upgrade_possible: canExpand ? "yes" : "already_max_plan"
};`,
          outputVariables: [
            { name: "signal_strength", type: "number" },
            { name: "signal_category", type: "string" },
            { name: "can_expand", type: "string" },
            { name: "engagement_summary", type: "string" },
            { name: "plan_upgrade_possible", type: "string" },
          ],
        },
      }),

      // 4. Fetch customer account data from HubSpot
      hubspotGetNode(
        "4",
        "Fetch Customer Account Data HubSpot",
        760,
        "crm/v3/objects/contacts/search?query={user_email}",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "hs_email",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "hs_company",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "csm_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "hs_lead_status",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "user_title",
            type: "string",
          },
        ],
      ),

      // 5. AI identify expansion signals
      aiNode(
        "5",
        "AI Identify Expansion Signals",
        1000,
        "You are a customer expansion intelligence analyst. Given a customer engagement signal profile, account details, and signal strength, analyze the expansion opportunity. Identify the most likely expansion path (plan upgrade, seat expansion, add-on purchase), the business need driving the interest, and suggest a personalized outreach approach. Respond JSON: {expansion_path: string, business_need: string, outreach_approach: string, expansion_email_subject: string, expansion_email_body: string, urgency_level: string}",
        "User: {user_name} ({user_email}) at {company_name}. Title: {user_title}. Current plan: {current_plan}. Feature engaged: {feature_engaged}. Engagement: {engagement_summary}. Signal strength: {signal_strength} ({signal_category}). Can expand: {can_expand}. Lead status: {hs_lead_status}. Context: {engagement_context}. Analyze the expansion opportunity.",
        [
          { field: "expansion_path", as: "expansion_path", type: "string" },
          { field: "business_need", as: "business_need", type: "string" },
          {
            field: "outreach_approach",
            as: "outreach_approach",
            type: "string",
          },
          {
            field: "expansion_email_subject",
            as: "expansion_email_subject",
            type: "string",
          },
          {
            field: "expansion_email_body",
            as: "expansion_email_body",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 6. Score expansion likelihood runCode
      n("6", "runCode", "Score Expansion Likelihood", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate final expansion score combining signal strength with account context
const signalStrength = parseInt(vars.signal_strength) || 0;
const canExpand = vars.can_expand === "true";
const urgency = (vars.urgency_level || "").toLowerCase();
let expansionScore = signalStrength;
if (!canExpand) expansionScore = Math.min(expansionScore, 20);
if (urgency === "high") expansionScore += 15;
else if (urgency === "medium") expansionScore += 8;
const expansionTier = expansionScore >= 70 ? "immediate_outreach" : expansionScore >= 40 ? "csm_nurture" : "monitor_only";
const routeToCSM = expansionScore >= 50 ? "true" : "false";
return {
  expansion_score: expansionScore,
  expansion_tier: expansionTier,
  route_to_csm: routeToCSM,
  expansion_priority: expansionScore >= 70 ? "P1" : expansionScore >= 40 ? "P2" : "P3"
};`,
          outputVariables: [
            { name: "expansion_score", type: "number" },
            { name: "expansion_tier", type: "string" },
            { name: "route_to_csm", type: "string" },
            { name: "expansion_priority", type: "string" },
          ],
        },
      }),

      // 7. Branch by expansion score
      n("7", "condition", "High Expansion Score?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "route_to_csm", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8a. Route high-intent to CSM via Slack
      slackNode(
        "8",
        "Route High-Intent to CSM Slack",
        1720,
        "[{expansion_priority}] Expansion Signal: *{user_name}* at *{company_name}*\\nFeature: {feature_engaged} | Signal: {signal_category} ({expansion_score})\\nPlan: {current_plan} | Path: {expansion_path}\\nBusiness need: {business_need}\\nApproach: {outreach_approach}\\nUrgency: {urgency_level}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1872. PRODUCT USAGE MILESTONE CELEBRATION (8 nodes)
  // ===================================================================

  "Product Usage Milestone Celebration": {
    summary:
      "Fires when a milestone event arrives via webhook, extracts milestone data with AI, fetches customer account details from HubSpot, generates milestone context with runCode, drafts a personalized celebration email with AI, branches by milestone tier, sends the celebration email, evaluates social celebration eligibility via condition, and logs the milestone to CRM in HubSpot.",
    nodes: [
      trigger("Webhook: Usage milestone event", "webhook_app", {
        source: "product_analytics",
        event: "user.milestone_reached",
        howItStarts:
          "Fires when a customer reaches a product usage milestone — such as 1000th workflow run, 100th team member added, 1 year anniversary, or a significant feature adoption threshold",
        setup:
          "Configure product analytics webhook for milestone events. Set OpenAI API key for celebration email drafting. Set HubSpot API token for account details and milestone logging. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract milestone data
      aiNode(
        "2",
        "AI Extract Milestone Event Data",
        280,
        "You are a product analytics data parser. Extract the user email, user name, company name, account ID, milestone type (usage_count, anniversary, team_growth, feature_adoption), milestone value (the number or date), milestone label (human-readable description), the product or feature involved, account plan, and the previous milestone if any from the raw webhook payload. Respond JSON: {user_email: string, user_name: string, company_name: string, account_id: string, milestone_type: string, milestone_value: string, milestone_label: string, product_feature: string, account_plan: string, previous_milestone: string}",
        "Raw webhook payload: {webhook_payload}. Extract all milestone event data regardless of field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "milestone_value", as: "milestone_value", type: "string" },
          { field: "milestone_label", as: "milestone_label", type: "string" },
          { field: "product_feature", as: "product_feature", type: "string" },
          { field: "account_plan", as: "account_plan", type: "string" },
          {
            field: "previous_milestone",
            as: "previous_milestone",
            type: "string",
          },
        ],
      ),

      // 3. Fetch customer account details from HubSpot
      hubspotGetNode(
        "3",
        "Fetch Customer Account Details HubSpot",
        520,
        "crm/v3/objects/contacts/search?query={user_email}",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "hs_company",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "csm_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "total_page_views",
            type: "string",
          },
        ],
      ),

      // 4. Generate milestone context runCode
      n("4", "runCode", "Generate Milestone Context", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine milestone tier and celebration intensity
const milestoneType = (vars.milestone_type || "").toLowerCase();
const milestoneValue = parseInt(vars.milestone_value) || 0;
const plan = (vars.account_plan || "").toLowerCase();
let milestoneTier = "standard";
let celebrationIntensity = "medium";
let socialWorthy = "false";
if (milestoneType === "anniversary") {
  if (milestoneValue >= 3) { milestoneTier = "legendary"; celebrationIntensity = "high"; socialWorthy = "true"; }
  else if (milestoneValue >= 1) { milestoneTier = "notable"; celebrationIntensity = "high"; socialWorthy = "true"; }
} else if (milestoneType === "usage_count") {
  if (milestoneValue >= 10000) { milestoneTier = "legendary"; celebrationIntensity = "high"; socialWorthy = "true"; }
  else if (milestoneValue >= 1000) { milestoneTier = "notable"; celebrationIntensity = "medium"; socialWorthy = "true"; }
  else { milestoneTier = "standard"; celebrationIntensity = "low"; }
} else if (milestoneType === "team_growth") {
  if (milestoneValue >= 100) { milestoneTier = "legendary"; socialWorthy = "true"; celebrationIntensity = "high"; }
  else if (milestoneValue >= 25) { milestoneTier = "notable"; celebrationIntensity = "medium"; }
}
const rewardType = milestoneTier === "legendary" ? "premium_swag_and_feature" : milestoneTier === "notable" ? "swag_package" : "digital_badge";
return {
  milestone_tier: milestoneTier,
  celebration_intensity: celebrationIntensity,
  social_worthy: socialWorthy,
  reward_type: rewardType,
  milestone_display: vars.milestone_label || (milestoneType + " " + milestoneValue),
  is_enterprise: plan === "enterprise" ? "true" : "false"
};`,
          outputVariables: [
            { name: "milestone_tier", type: "string" },
            { name: "celebration_intensity", type: "string" },
            { name: "social_worthy", type: "string" },
            { name: "reward_type", type: "string" },
            { name: "milestone_display", type: "string" },
            { name: "is_enterprise", type: "string" },
          ],
        },
      }),

      // 5. AI personalized celebration email
      aiNode(
        "5",
        "AI Personalized Celebration Email",
        1000,
        "You are a customer success celebration specialist. Given a customer milestone, tier, and context, craft a genuine and enthusiastic celebration email. For legendary milestones, make it especially memorable with stats and recognition. For notable milestones, be warm and encouraging. Include a mention of their specific achievement and reward. Respond JSON: {celebration_subject: string, celebration_body_html: string, social_shoutout: string, next_milestone_teaser: string}",
        "Customer: {user_name} ({first_name}) at {company_name}. Milestone: {milestone_display} ({milestone_type}). Value: {milestone_value}. Feature: {product_feature}. Tier: {milestone_tier}. Intensity: {celebration_intensity}. Reward: {reward_type}. Plan: {account_plan}. Previous milestone: {previous_milestone}. Craft the celebration.",
        [
          {
            field: "celebration_subject",
            as: "celebration_subject",
            type: "string",
          },
          {
            field: "celebration_body_html",
            as: "celebration_body_html",
            type: "string",
          },
          { field: "social_shoutout", as: "social_shoutout", type: "string" },
          {
            field: "next_milestone_teaser",
            as: "next_milestone_teaser",
            type: "string",
          },
        ],
      ),

      // 6. Branch by milestone tier
      n("6", "condition", "Notable or Legendary Milestone?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "milestone_tier",
              operator: "in",
              value: "notable,legendary",
            },
          ],
        },
      }),

      // 7. Send celebration email
      emailNode(
        "7",
        "Send Celebration Email",
        1480,
        "{user_email}",
        "{celebration_subject}",
        "{celebration_body_html}<p>Your reward: {reward_type}</p><p>{next_milestone_teaser}</p>",
      ),

      // 8. Log milestone to CRM HubSpot
      hubspotPostNode(
        "8",
        "Log Milestone CRM HubSpot",
        1720,
        "crm/v3/objects/contacts/{hs_contact_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_content_membership_notes":"Milestone: {milestone_display} ({milestone_tier}) — {reward_type} sent","notes_last_updated":"Celebrated {milestone_type} milestone: {milestone_value}"}',
          },
        ],
        [
          { name: "id", customName: "updated_contact_id", type: "string" },
          {
            name: "properties.hs_lastmodifieddate",
            customName: "contact_updated_at",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1873. PRODUCT-QUALIFIED EVENT OUTBOUND (9 nodes)
  // ===================================================================

  "Product-Qualified Event Outbound": {
    summary:
      "Fires when a PQL signal arrives via webhook, extracts PQL data with AI, fetches user account data from HubSpot, validates PQL criteria with runCode, enriches the company profile via Clearbit, drafts usage-aware outreach with AI, scores and ranks the PQL with runCode, branches by PQL score, sends PQL outreach email, and creates a sales opportunity in HubSpot.",
    nodes: [
      trigger("Webhook: Product-qualified lead signal", "webhook_app", {
        source: "product_analytics",
        event: "user.pql_qualified",
        howItStarts:
          "Fires when a user crosses the product-qualified lead threshold — high feature adoption, team invites sent, integrations configured, or usage patterns that indicate strong buying intent",
        setup:
          "Configure product analytics webhook for PQL signals. Set OpenAI API key for outreach drafting. Set HubSpot API token for account data and opportunity creation. Set Clearbit API key for company enrichment. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract PQL data
      aiNode(
        "2",
        "AI Extract PQL Signal Data",
        280,
        "You are a product-led growth analytics parser. Extract the user email, user name, company name, account ID, the PQL trigger event (feature_adoption, team_invites, integration_setup, usage_threshold), the specific metric that triggered PQL (e.g., 5 integrations connected), current plan, number of team members, total feature usage count, signup date, and the product area most used from the raw webhook payload. Respond JSON: {user_email: string, user_name: string, company_name: string, account_id: string, pql_trigger: string, trigger_metric: string, current_plan: string, team_size: string, feature_usage_count: string, signup_date: string, top_product_area: string}",
        "Raw webhook payload: {webhook_payload}. Extract all PQL signal data regardless of field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "account_id", as: "account_id", type: "string" },
          { field: "pql_trigger", as: "pql_trigger", type: "string" },
          { field: "trigger_metric", as: "trigger_metric", type: "string" },
          { field: "current_plan", as: "current_plan", type: "string" },
          { field: "team_size", as: "team_size", type: "string" },
          {
            field: "feature_usage_count",
            as: "feature_usage_count",
            type: "string",
          },
          { field: "signup_date", as: "signup_date", type: "string" },
          { field: "top_product_area", as: "top_product_area", type: "string" },
        ],
      ),

      // 3. Fetch user account data from HubSpot
      hubspotGetNode(
        "3",
        "Fetch User Account Data HubSpot",
        520,
        "crm/v3/objects/contacts/search?query={user_email}",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "user_title",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "hs_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 4. Validate PQL criteria runCode
      n("4", "runCode", "Validate PQL Criteria", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and score the PQL based on multiple criteria
const teamSize = parseInt(vars.team_size) || 1;
const featureUsage = parseInt(vars.feature_usage_count) || 0;
const pqlTrigger = (vars.pql_trigger || "").toLowerCase();
const currentPlan = (vars.current_plan || "").toLowerCase();
const signupDate = new Date(vars.signup_date || Date.now());
const daysActive = Math.floor((Date.now() - signupDate.getTime()) / (1000 * 60 * 60 * 24));
let pqlScore = 0;
if (pqlTrigger === "integration_setup") pqlScore += 25;
else if (pqlTrigger === "team_invites") pqlScore += 20;
else if (pqlTrigger === "feature_adoption") pqlScore += 15;
else pqlScore += 10;
if (teamSize >= 10) pqlScore += 25;
else if (teamSize >= 5) pqlScore += 15;
else pqlScore += 5;
if (featureUsage >= 100) pqlScore += 25;
else if (featureUsage >= 50) pqlScore += 15;
else pqlScore += 5;
if (daysActive <= 14) pqlScore += 15;
else if (daysActive <= 30) pqlScore += 10;
const isValidPql = pqlScore >= 40 && currentPlan !== "enterprise";
const pqlTier = pqlScore >= 75 ? "hot" : pqlScore >= 50 ? "warm" : "emerging";
return {
  pql_score: pqlScore,
  pql_tier: pqlTier,
  is_valid_pql: isValidPql ? "true" : "false",
  days_active: daysActive,
  velocity_label: daysActive <= 14 ? "fast_adopter" : daysActive <= 30 ? "steady" : "gradual"
};`,
          outputVariables: [
            { name: "pql_score", type: "number" },
            { name: "pql_tier", type: "string" },
            { name: "is_valid_pql", type: "string" },
            { name: "days_active", type: "number" },
            { name: "velocity_label", type: "string" },
          ],
        },
      }),

      // 5. Enrich company profile via Clearbit
      n("5", "hitApi", "Enrich Company Profile Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "enriched_employee_count",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            { name: "tech", customName: "enriched_tech_stack", type: "string" },
            {
              name: "metrics.raised",
              customName: "enriched_funding",
              type: "string",
            },
          ],
        },
      }),

      // 6. AI usage-aware outreach
      aiNode(
        "6",
        "AI Usage-Aware PQL Outreach",
        1240,
        "You are a product-led sales specialist. Given a PQL profile with their specific product usage patterns, PQL score, company enrichment data, and trigger event, craft a hyper-personalized outreach email that references their specific usage and naturally leads to a conversation about upgrading or expanding. Never be pushy — lead with value and insights about their usage patterns. Respond JSON: {pql_email_subject: string, pql_email_body: string, value_hook: string, suggested_plan: string, meeting_cta: string}",
        "User: {user_name} ({first_name} {last_name}, {user_title}) at {company_name}. PQL trigger: {pql_trigger} ({trigger_metric}). Score: {pql_score} ({pql_tier}). Velocity: {velocity_label}. Team size: {team_size}. Feature usage: {feature_usage_count}. Top area: {top_product_area}. Current plan: {current_plan}. Industry: {enriched_industry}. Employees: {enriched_employee_count}. Revenue: {enriched_revenue}. Tech stack: {enriched_tech_stack}. Days active: {days_active}. Craft the outreach.",
        [
          {
            field: "pql_email_subject",
            as: "pql_email_subject",
            type: "string",
          },
          { field: "pql_email_body", as: "pql_email_body", type: "string" },
          { field: "value_hook", as: "value_hook", type: "string" },
          { field: "suggested_plan", as: "suggested_plan", type: "string" },
          { field: "meeting_cta", as: "meeting_cta", type: "string" },
        ],
      ),

      // 7. Score and rank PQL runCode
      n("7", "runCode", "Score Rank PQL Priority", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Final ranking with enrichment data factored in
const pqlScore = parseInt(vars.pql_score) || 0;
const employees = parseInt(vars.enriched_employee_count) || 0;
const revenue = vars.enriched_revenue || "";
let finalScore = pqlScore;
if (employees > 500) finalScore += 15;
else if (employees > 100) finalScore += 10;
else if (employees > 20) finalScore += 5;
if (revenue.includes("$10M") || revenue.includes("$50M") || revenue.includes("$100M")) finalScore += 10;
const finalTier = finalScore >= 80 ? "priority_1" : finalScore >= 55 ? "priority_2" : "priority_3";
const sendOutreach = finalScore >= 45 ? "true" : "false";
return {
  final_pql_score: finalScore,
  final_tier: finalTier,
  send_outreach: sendOutreach,
  deal_potential: employees > 200 ? "enterprise" : employees > 50 ? "mid_market" : "smb"
};`,
          outputVariables: [
            { name: "final_pql_score", type: "number" },
            { name: "final_tier", type: "string" },
            { name: "send_outreach", type: "string" },
            { name: "deal_potential", type: "string" },
          ],
        },
      }),

      // 8. Branch by PQL score
      n("8", "condition", "PQL Score Qualifies for Outreach?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "send_outreach", operator: "eq", value: "true" },
          ],
        },
      }),

      // 9. Send PQL outreach email + create opportunity
      emailNode(
        "9",
        "Send PQL Outreach Email",
        1960,
        "{user_email}",
        "{pql_email_subject}",
        "{pql_email_body}<p>{meeting_cta}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1874. PROMOTION TRIGGER OUTREACH (8 nodes)
  // ===================================================================

  "Promotion Trigger Outreach": {
    summary:
      "Fires when a promotion signal arrives via webhook, extracts promotion data with AI, validates promotion details with runCode, fetches historical relationship data from HubSpot, drafts a congratulatory outreach with AI, branches by relationship history, validates the email via API, sends a congratulations email, and updates CRM records in HubSpot.",
    nodes: [
      trigger("Webhook: Contact promotion detected", "webhook_app", {
        source: "sales_intelligence",
        event: "contact.promoted",
        howItStarts:
          "Fires when a sales intelligence tool detects a key contact has been promoted — signals a major re-engagement opportunity as newly promoted executives often bring new budgets, priorities, and vendor evaluations",
        setup:
          "Configure sales intelligence webhook for promotion signals. Set OpenAI API key for congratulatory outreach. Set HubSpot API token for relationship data and CRM updates. Set ZeroBounce API key for email validation. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract promotion data
      aiNode(
        "2",
        "AI Extract Promotion Signal Data",
        280,
        "You are a sales intelligence data parser. Extract the contact email, contact name, company name, company domain, new title, previous title, promotion date, department, LinkedIn URL, the data source (linkedin, press_release, company_page), and any additional context from the raw webhook payload. Respond JSON: {contact_email: string, contact_name: string, company_name: string, company_domain: string, new_title: string, previous_title: string, promotion_date: string, department: string, linkedin_url: string, data_source: string, promotion_context: string}",
        "Raw webhook payload: {webhook_payload}. Extract all promotion signal data regardless of field naming conventions.",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "new_title", as: "new_title", type: "string" },
          { field: "previous_title", as: "previous_title", type: "string" },
          { field: "promotion_date", as: "promotion_date", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "linkedin_url", as: "linkedin_url", type: "string" },
          { field: "data_source", as: "data_source", type: "string" },
          {
            field: "promotion_context",
            as: "promotion_context",
            type: "string",
          },
        ],
      ),

      // 3. Validate promotion details runCode
      n("3", "runCode", "Validate Promotion Details", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze promotion significance and determine outreach priority
const newTitle = (vars.new_title || "").toLowerCase();
const prevTitle = (vars.previous_title || "").toLowerCase();
const isCSuite = newTitle.includes("chief") || newTitle.includes("ceo") || newTitle.includes("cto") || newTitle.includes("cmo") || newTitle.includes("cfo") || newTitle.includes("cro");
const isVP = newTitle.includes("vp") || newTitle.includes("vice president");
const isDirector = newTitle.includes("director");
const isManager = newTitle.includes("manager") || newTitle.includes("head of");
let seniorityLevel = "individual_contributor";
if (isCSuite) seniorityLevel = "c_suite";
else if (isVP) seniorityLevel = "vp";
else if (isDirector) seniorityLevel = "director";
else if (isManager) seniorityLevel = "manager";
const promotionSignificance = isCSuite ? "major" : isVP ? "significant" : isDirector ? "notable" : "standard";
const budgetAuthority = isCSuite || isVP ? "high" : isDirector ? "medium" : "low";
const outreachPriority = promotionSignificance === "major" ? "immediate" : promotionSignificance === "significant" ? "within_24h" : "within_week";
return {
  seniority_level: seniorityLevel,
  promotion_significance: promotionSignificance,
  budget_authority: budgetAuthority,
  outreach_priority: outreachPriority,
  title_change: prevTitle + " -> " + newTitle,
  is_decision_maker: budgetAuthority !== "low" ? "true" : "false"
};`,
          outputVariables: [
            { name: "seniority_level", type: "string" },
            { name: "promotion_significance", type: "string" },
            { name: "budget_authority", type: "string" },
            { name: "outreach_priority", type: "string" },
            { name: "title_change", type: "string" },
            { name: "is_decision_maker", type: "string" },
          ],
        },
      }),

      // 4. Fetch historical relationship from HubSpot
      hubspotGetNode(
        "4",
        "Fetch Historical Relationship HubSpot",
        760,
        "crm/v3/objects/contacts/search?query={contact_email}",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "past_deals_count",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_visits",
            customName: "total_visits",
            type: "string",
          },
        ],
      ),

      // 5. AI congratulatory outreach
      aiNode(
        "5",
        "AI Congratulatory Outreach Email",
        1000,
        "You are a relationship-first sales professional. Given a contact who has been promoted, their history in your CRM, and the promotion significance, craft a genuinely warm congratulatory email. Do NOT pitch immediately — lead with celebration, mention their career trajectory, and subtly re-open the relationship. If they are an existing contact, reference the past relationship. If new, make a warm introduction. Respond JSON: {congrats_subject: string, congrats_body_html: string, relationship_angle: string, soft_cta: string, follow_up_timing: string}",
        "Contact: {contact_name} at {company_name}. Promotion: {title_change}. Department: {department}. Seniority: {seniority_level}. Significance: {promotion_significance}. Budget authority: {budget_authority}. LinkedIn: {linkedin_url}. CRM status: {lead_status}. Past deals: {past_deals_count}. Last contacted: {last_contacted}. Context: {promotion_context}. Draft the congratulations.",
        [
          { field: "congrats_subject", as: "congrats_subject", type: "string" },
          {
            field: "congrats_body_html",
            as: "congrats_body_html",
            type: "string",
          },
          {
            field: "relationship_angle",
            as: "relationship_angle",
            type: "string",
          },
          { field: "soft_cta", as: "soft_cta", type: "string" },
          { field: "follow_up_timing", as: "follow_up_timing", type: "string" },
        ],
      ),

      // 6. Branch by relationship history
      n("6", "condition", "Existing CRM Contact?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "hs_contact_id", operator: "neq", value: "" },
          ],
        },
      }),

      // 7. Validate email via ZeroBounce
      n("7", "hitApi", "Validate Contact Email", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={contact_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "status", customName: "email_status", type: "string" },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Send congratulations email + update CRM
      emailNode(
        "8",
        "Send Congratulations Email",
        1720,
        "{contact_email}",
        "{congrats_subject}",
        "{congrats_body_html}<p>{soft_cta}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1875. QUARTERLY ACCOUNT REVIEW PREP (9 nodes)
  // ===================================================================

  "Quarterly Account Review Prep": {
    summary:
      "Runs on a schedule 14 days before QBR, pulls account usage metrics from HubSpot, fetches financial data with runCode, analyzes account health with AI, generates a QBR presentation outline with AI, builds a stakeholder briefing with runCode, branches by account health, creates a QBR deck via Google Slides API, and notifies the CSM via Slack.",
    nodes: [
      trigger("Schedule: QBR prep 14 days before", "schedule", {
        schedule: "0 8 1 */3 *",
        howItStarts:
          "Runs quarterly to prepare account review materials 14 days before the scheduled QBR date — automatically generates health analysis, presentation outlines, and stakeholder briefings for upcoming quarterly business reviews",
        setup:
          "Configure HubSpot API token for account metrics. Set OpenAI API key for health analysis and presentation generation. Set Google Slides API credentials for deck creation. Configure Slack webhook for CSM notifications.",
        inputFields: [],
      }),

      // 2. Pull account usage metrics from HubSpot
      hubspotGetNode(
        "2",
        "Pull Account Usage Metrics HubSpot",
        280,
        "crm/v3/objects/companies?limit=1&properties=name,industry,numberofemployees,annualrevenue,hs_lead_status,notes_last_contacted,num_associated_deals,lifecyclestage",
        [
          { name: "results[0].id", customName: "company_id", type: "string" },
          {
            name: "results[0].properties.name",
            customName: "company_name",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "industry",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "employee_count",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "annual_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "total_deals",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
        ],
      ),

      // 3. Fetch account financial data runCode
      n("3", "runCode", "Fetch Account Financial Summary", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build financial summary from available account data
const revenue = parseFloat(vars.annual_revenue) || 0;
const deals = parseInt(vars.total_deals) || 0;
const lastContacted = vars.last_contacted ? new Date(vars.last_contacted) : null;
const daysSinceContact = lastContacted ? Math.floor((Date.now() - lastContacted.getTime()) / (1000 * 60 * 60 * 24)) : -1;
const avgDealValue = deals > 0 ? Math.round(revenue / deals) : 0;
const qbrQuarter = "Q" + (Math.floor(new Date().getMonth() / 3) + 1) + " " + new Date().getFullYear();
const renewalUrgency = daysSinceContact > 60 ? "high" : daysSinceContact > 30 ? "medium" : "low";
const accountHealthIndicator = daysSinceContact < 14 ? "engaged" : daysSinceContact < 30 ? "active" : daysSinceContact < 60 ? "cooling" : "at_risk";
return {
  avg_deal_value: avgDealValue,
  qbr_quarter: qbrQuarter,
  days_since_contact: daysSinceContact,
  renewal_urgency: renewalUrgency,
  account_health_indicator: accountHealthIndicator,
  revenue_display: "$" + revenue.toLocaleString(),
  qbr_date_target: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "avg_deal_value", type: "number" },
            { name: "qbr_quarter", type: "string" },
            { name: "days_since_contact", type: "number" },
            { name: "renewal_urgency", type: "string" },
            { name: "account_health_indicator", type: "string" },
            { name: "revenue_display", type: "string" },
            { name: "qbr_date_target", type: "string" },
          ],
        },
      }),

      // 4. AI analyze account health
      aiNode(
        "4",
        "AI Analyze Account Health",
        760,
        "You are a customer success analyst preparing for a quarterly business review. Given account metrics including revenue, deal history, engagement level, and health indicators, perform a comprehensive health analysis. Identify strengths, risks, expansion opportunities, and churn signals. Respond JSON: {health_score: string, strengths: string, risks: string, expansion_opportunities: string, churn_signals: string, recommended_focus_areas: string, executive_summary: string}",
        "Company: {company_name} ({industry}, {employee_count} employees). Revenue: {revenue_display}. Deals: {total_deals}. Avg deal: ${avg_deal_value}. Lifecycle: {lifecycle_stage}. Health: {account_health_indicator}. Days since contact: {days_since_contact}. Renewal urgency: {renewal_urgency}. Quarter: {qbr_quarter}. Analyze this account health.",
        [
          { field: "health_score", as: "health_score", type: "string" },
          { field: "strengths", as: "account_strengths", type: "string" },
          { field: "risks", as: "account_risks", type: "string" },
          {
            field: "expansion_opportunities",
            as: "expansion_opportunities",
            type: "string",
          },
          { field: "churn_signals", as: "churn_signals", type: "string" },
          {
            field: "recommended_focus_areas",
            as: "focus_areas",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
        ],
      ),

      // 5. AI QBR presentation outline
      aiNode(
        "5",
        "AI QBR Presentation Outline",
        1000,
        "You are a customer success presentation designer. Given the account health analysis, financial summary, and quarterly context, create a detailed QBR presentation outline with slide titles, key talking points for each slide, and data points to highlight. Include sections for review of last quarter, usage metrics, ROI achieved, health assessment, and forward-looking plans. Respond JSON: {presentation_title: string, slide_outline: string, key_metrics_slide: string, roi_highlights: string, action_items_slide: string, next_quarter_goals: string}",
        "Company: {company_name}. Quarter: {qbr_quarter}. Health score: {health_score}. Summary: {executive_summary}. Strengths: {account_strengths}. Risks: {account_risks}. Expansion: {expansion_opportunities}. Focus areas: {focus_areas}. Revenue: {revenue_display}. Build the QBR deck outline.",
        [
          {
            field: "presentation_title",
            as: "presentation_title",
            type: "string",
          },
          { field: "slide_outline", as: "slide_outline", type: "string" },
          {
            field: "key_metrics_slide",
            as: "key_metrics_slide",
            type: "string",
          },
          { field: "roi_highlights", as: "roi_highlights", type: "string" },
          {
            field: "action_items_slide",
            as: "action_items_slide",
            type: "string",
          },
          {
            field: "next_quarter_goals",
            as: "next_quarter_goals",
            type: "string",
          },
        ],
      ),

      // 6. Build stakeholder briefing runCode
      n("6", "runCode", "Build Stakeholder Briefing", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble stakeholder-ready briefing document
const healthScore = vars.health_score || "unknown";
const healthNum = parseInt(healthScore) || 0;
const briefingSections = [
  "EXECUTIVE BRIEF: " + vars.executive_summary,
  "HEALTH: " + healthScore + " (" + vars.account_health_indicator + ")",
  "STRENGTHS: " + vars.account_strengths,
  "RISKS: " + vars.account_risks,
  "EXPANSION: " + vars.expansion_opportunities,
  "FOCUS AREAS: " + vars.focus_areas
].join(" | ");
const isHealthy = healthNum >= 70 || vars.account_health_indicator === "engaged" || vars.account_health_indicator === "active";
const briefingTone = isHealthy ? "positive_growth" : "recovery_focused";
const prepStatus = "ready_for_review";
return {
  stakeholder_briefing: briefingSections,
  briefing_tone: briefingTone,
  prep_status: prepStatus,
  account_healthy: isHealthy ? "true" : "false",
  qbr_readiness: "materials_generated"
};`,
          outputVariables: [
            { name: "stakeholder_briefing", type: "string" },
            { name: "briefing_tone", type: "string" },
            { name: "prep_status", type: "string" },
            { name: "account_healthy", type: "string" },
            { name: "qbr_readiness", type: "string" },
          ],
        },
      }),

      // 7. Branch by account health
      n("7", "condition", "Account Health Positive?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "account_healthy", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8. Create QBR deck via Google Slides API
      n("8", "hitApi", "Create QBR Deck Google Slides", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://slides.googleapis.com/v1/presentations",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "title", value: "{presentation_title}" }],
          variables: [
            {
              name: "presentationId",
              customName: "slides_deck_id",
              type: "string",
            },
            {
              name: "slides[0].objectId",
              customName: "first_slide_id",
              type: "string",
            },
          ],
        },
      }),

      // 9. Notify CSM via Slack
      slackNode(
        "9",
        "Notify CSM QBR Prep Ready Slack",
        1960,
        "QBR Prep Complete: *{company_name}* ({qbr_quarter})\\nHealth: {health_score} ({account_health_indicator}) | Tone: {briefing_tone}\\nDeck: https://docs.google.com/presentation/d/{slides_deck_id}\\nTarget date: {qbr_date_target}\\nFocus: {focus_areas}\\nExpansion: {expansion_opportunities}\\nRisks: {account_risks}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1876. RFP CONTACT EXPANSION (9 nodes)
  // ===================================================================

  "RFP Contact Expansion": {
    summary:
      "Fires when an RFP receipt signal arrives via webhook, extracts RFP data with AI, parses the RFP document with runCode, searches the evaluation committee via API, enriches committee contacts via Clearbit, discovers email addresses via Hunter API, drafts role-specific engagement emails with AI, branches by committee role, sends role-specific outreach, and creates a multi-thread tracker in HubSpot.",
    nodes: [
      trigger("Webhook: RFP received signal", "webhook_app", {
        source: "procurement_inbox",
        event: "rfp.received",
        howItStarts:
          "Fires when an RFP is received from a prospect — kicks off a multi-threaded contact expansion campaign to engage every member of the evaluation committee with role-specific messaging",
        setup:
          "Configure procurement inbox webhook for RFP receipts. Set OpenAI API key for RFP parsing and outreach drafting. Set Clearbit API key for contact enrichment. Set Hunter.io API key for email discovery. Set HubSpot API token for tracker creation. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract RFP data
      aiNode(
        "2",
        "AI Extract RFP Receipt Data",
        280,
        "You are an RFP analysis specialist. Extract the company name, company domain, RFP title, submission deadline, key requirements summary, evaluation criteria, budget range if mentioned, the sender name and email, number of evaluation committee members if stated, and the product categories requested from the raw webhook payload. Respond JSON: {company_name: string, company_domain: string, rfp_title: string, submission_deadline: string, requirements_summary: string, evaluation_criteria: string, budget_range: string, sender_name: string, sender_email: string, committee_size: string, product_categories: string}",
        "Raw webhook payload: {webhook_payload}. Extract all RFP receipt data regardless of field naming conventions.",
        [
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "rfp_title", as: "rfp_title", type: "string" },
          {
            field: "submission_deadline",
            as: "submission_deadline",
            type: "string",
          },
          {
            field: "requirements_summary",
            as: "requirements_summary",
            type: "string",
          },
          {
            field: "evaluation_criteria",
            as: "evaluation_criteria",
            type: "string",
          },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "committee_size", as: "committee_size", type: "string" },
          {
            field: "product_categories",
            as: "product_categories",
            type: "string",
          },
        ],
      ),

      // 3. Parse RFP document runCode
      n("3", "runCode", "Parse RFP Document Structure", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze RFP structure and identify key sections for response strategy
const requirements = (vars.requirements_summary || "").split(",").map(r => r.trim()).filter(Boolean);
const criteria = (vars.evaluation_criteria || "").split(",").map(c => c.trim()).filter(Boolean);
const deadline = new Date(vars.submission_deadline || Date.now());
const daysUntilDeadline = Math.floor((deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
const committeeSize = parseInt(vars.committee_size) || 3;
const urgency = daysUntilDeadline <= 7 ? "critical" : daysUntilDeadline <= 14 ? "high" : daysUntilDeadline <= 30 ? "medium" : "standard";
const responseComplexity = requirements.length > 10 ? "complex" : requirements.length > 5 ? "moderate" : "straightforward";
const stakeholderRoles = ["procurement_lead", "technical_evaluator", "business_sponsor"];
if (committeeSize > 3) stakeholderRoles.push("finance_reviewer");
if (committeeSize > 5) stakeholderRoles.push("legal_reviewer", "executive_sponsor");
return {
  requirement_count: requirements.length,
  criteria_count: criteria.length,
  days_until_deadline: daysUntilDeadline,
  urgency_level: urgency,
  response_complexity: responseComplexity,
  target_roles: stakeholderRoles.join(", "),
  thread_count: Math.min(committeeSize, stakeholderRoles.length),
  rfp_id: "RFP-" + Date.now().toString(36).toUpperCase()
};`,
          outputVariables: [
            { name: "requirement_count", type: "number" },
            { name: "criteria_count", type: "number" },
            { name: "days_until_deadline", type: "number" },
            { name: "urgency_level", type: "string" },
            { name: "response_complexity", type: "string" },
            { name: "target_roles", type: "string" },
            { name: "thread_count", type: "number" },
            { name: "rfp_id", type: "string" },
          ],
        },
      }),

      // 4. Search evaluation committee via Hunter.io
      n("4", "hitApi", "Search Evaluation Committee Contacts", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={company_domain}&api_key={hunter_api_key}&limit=10",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "committee_email_1",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "committee_first_1",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "committee_last_1",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "committee_role_1",
              type: "string",
            },
            {
              name: "data.emails[0].department",
              customName: "committee_dept_1",
              type: "string",
            },
            {
              name: "meta.results",
              customName: "contacts_found",
              type: "number",
            },
          ],
        },
      }),

      // 5. Enrich committee contacts via Clearbit
      n("5", "hitApi", "Enrich Committee Contacts Clearbit", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={committee_email_1}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            { name: "title", customName: "enriched_title", type: "string" },
            { name: "role", customName: "enriched_role", type: "string" },
            {
              name: "seniority",
              customName: "enriched_seniority",
              type: "string",
            },
            {
              name: "linkedin.handle",
              customName: "enriched_linkedin",
              type: "string",
            },
          ],
        },
      }),

      // 6. Discover email addresses via Hunter
      n("6", "hitApi", "Discover Committee Email Addresses", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/email-verifier?email={committee_email_1}&api_key={hunter_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.status",
              customName: "email_verification_status",
              type: "string",
            },
            {
              name: "data.score",
              customName: "email_verification_score",
              type: "number",
            },
            {
              name: "data.result",
              customName: "email_verification_result",
              type: "string",
            },
          ],
        },
      }),

      // 7. AI role-specific engagement emails
      aiNode(
        "7",
        "AI Role-Specific Engagement Emails",
        1480,
        "You are an RFP response strategist. Given a committee member role, seniority, the RFP context, and evaluation criteria, craft a role-specific engagement email. For procurement leads, focus on compliance and process alignment. For technical evaluators, emphasize technical capabilities. For business sponsors, highlight ROI and strategic fit. Respond JSON: {role_email_subject: string, role_email_body: string, value_proposition: string, role_specific_hook: string, next_step_suggestion: string}",
        "RFP: {rfp_title} from {company_name}. Deadline: {submission_deadline} ({days_until_deadline} days). Committee member: {enriched_name} ({enriched_title}, {enriched_seniority}). Role: {enriched_role}. Department: {committee_dept_1}. Requirements: {requirements_summary}. Criteria: {evaluation_criteria}. Budget: {budget_range}. Complexity: {response_complexity}. Urgency: {urgency_level}. Craft the role-specific email.",
        [
          {
            field: "role_email_subject",
            as: "role_email_subject",
            type: "string",
          },
          { field: "role_email_body", as: "role_email_body", type: "string" },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          {
            field: "role_specific_hook",
            as: "role_specific_hook",
            type: "string",
          },
          {
            field: "next_step_suggestion",
            as: "next_step_suggestion",
            type: "string",
          },
        ],
      ),

      // 8. Branch by committee role (technical vs business)
      n("8", "condition", "Technical Evaluator Role?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "enriched_role", operator: "eq", value: "engineering" },
          ],
        },
      }),

      // 9. Send role-specific outreach email + create tracker
      emailNode(
        "9",
        "Send Role-Specific Outreach Email",
        1960,
        "{committee_email_1}",
        "{role_email_subject}",
        "{role_email_body}<p>{value_proposition}</p><p>Next step: {next_step_suggestion}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("8", "9", "output_2"),
    ],
  },

  // ===================================================================
  // 1877. ROI PERSONALIZATION ENGINE (8 nodes)
  // ===================================================================

  "ROI Personalization Engine": {
    summary:
      "Fires when an ROI request arrives via webhook, extracts request data with AI, fetches prospect company data via Clearbit, pulls industry benchmark data with runCode, estimates current spend with runCode, calculates personalized ROI with runCode, generates an ROI narrative with AI, branches by deal size, and delivers the ROI package via email with HubSpot logging.",
    nodes: [
      trigger("Webhook: ROI calculation request", "webhook_app", {
        source: "sales_tools",
        event: "roi.calculation_requested",
        howItStarts:
          "Fires when a sales rep or prospect requests a personalized ROI calculation — typically from a landing page form, chatbot interaction, or sales tool trigger that captures company details and current spend information",
        setup:
          "Configure sales tools webhook for ROI requests. Set OpenAI API key for ROI narrative generation. Set Clearbit API key for company enrichment. Set HubSpot API token for deal logging. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. AI extract ROI request data
      aiNode(
        "2",
        "AI Extract ROI Request Data",
        280,
        "You are a sales operations data parser. Extract the prospect email, prospect name, company name, company domain, industry, current tool or solution in use, estimated monthly spend, number of users, primary pain point, deal stage if known, and the sales rep email from the raw webhook payload. Respond JSON: {prospect_email: string, prospect_name: string, company_name: string, company_domain: string, industry: string, current_solution: string, monthly_spend: string, user_count: string, primary_pain_point: string, deal_stage: string, rep_email: string}",
        "Raw webhook payload: {webhook_payload}. Extract all ROI request data regardless of field naming conventions.",
        [
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "current_solution", as: "current_solution", type: "string" },
          { field: "monthly_spend", as: "monthly_spend", type: "string" },
          { field: "user_count", as: "user_count", type: "string" },
          {
            field: "primary_pain_point",
            as: "primary_pain_point",
            type: "string",
          },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
        ],
      ),

      // 3. Fetch prospect company data via Clearbit
      n("3", "hitApi", "Fetch Prospect Company Data Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "enriched_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "employee_count",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "est_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            {
              name: "category.sector",
              customName: "company_sector",
              type: "string",
            },
            { name: "tech", customName: "tech_stack", type: "string" },
          ],
        },
      }),

      // 4. Pull industry benchmark data runCode
      n("4", "runCode", "Pull Industry Benchmark Data", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Industry benchmark lookup for ROI calculations
const industry = (vars.enriched_industry || vars.industry || "").toLowerCase();
const benchmarks = {
  "saas": { avgCostPerUser: 85, avgTimeWasted: 12, avgChurnReduction: 15, avgRevenueIncrease: 22 },
  "technology": { avgCostPerUser: 95, avgTimeWasted: 10, avgChurnReduction: 12, avgRevenueIncrease: 20 },
  "financial services": { avgCostPerUser: 120, avgTimeWasted: 15, avgChurnReduction: 18, avgRevenueIncrease: 25 },
  "healthcare": { avgCostPerUser: 110, avgTimeWasted: 18, avgChurnReduction: 20, avgRevenueIncrease: 18 },
  "retail": { avgCostPerUser: 65, avgTimeWasted: 14, avgChurnReduction: 22, avgRevenueIncrease: 28 },
  "manufacturing": { avgCostPerUser: 75, avgTimeWasted: 16, avgChurnReduction: 10, avgRevenueIncrease: 15 }
};
const industryData = benchmarks[industry] || benchmarks["technology"];
return {
  benchmark_cost_per_user: industryData.avgCostPerUser,
  benchmark_time_wasted_hrs: industryData.avgTimeWasted,
  benchmark_churn_reduction_pct: industryData.avgChurnReduction,
  benchmark_revenue_increase_pct: industryData.avgRevenueIncrease,
  benchmark_source: industry in benchmarks ? industry : "technology (default)",
  data_confidence: industry in benchmarks ? "high" : "moderate"
};`,
          outputVariables: [
            { name: "benchmark_cost_per_user", type: "number" },
            { name: "benchmark_time_wasted_hrs", type: "number" },
            { name: "benchmark_churn_reduction_pct", type: "number" },
            { name: "benchmark_revenue_increase_pct", type: "number" },
            { name: "benchmark_source", type: "string" },
            { name: "data_confidence", type: "string" },
          ],
        },
      }),

      // 5. Estimate current spend and calculate ROI runCode
      n("5", "runCode", "Calculate Personalized ROI", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate personalized ROI based on prospect data and benchmarks
const monthlySpend = parseFloat(vars.monthly_spend) || 0;
const userCount = parseInt(vars.user_count) || 10;
const employees = parseInt(vars.employee_count) || userCount;
const benchmarkCost = parseInt(vars.benchmark_cost_per_user) || 85;
const timeWasted = parseInt(vars.benchmark_time_wasted_hrs) || 12;
const churnReduction = parseInt(vars.benchmark_churn_reduction_pct) || 15;
const revenueIncrease = parseInt(vars.benchmark_revenue_increase_pct) || 20;
const estimatedCurrentCost = monthlySpend > 0 ? monthlySpend * 12 : userCount * benchmarkCost * 12;
const ourAnnualCost = userCount * 45 * 12;
const annualSavings = estimatedCurrentCost - ourAnnualCost;
const timeRecovered = userCount * timeWasted * 12;
const timeValueSaved = timeRecovered * 50;
const churnValueSaved = Math.round(estimatedCurrentCost * (churnReduction / 100));
const revenueGain = Math.round(estimatedCurrentCost * (revenueIncrease / 100));
const totalROIValue = annualSavings + timeValueSaved + churnValueSaved + revenueGain;
const roiMultiplier = ourAnnualCost > 0 ? Math.round((totalROIValue / ourAnnualCost) * 10) / 10 : 0;
const dealSize = ourAnnualCost > 100000 ? "enterprise" : ourAnnualCost > 25000 ? "mid_market" : "smb";
return {
  estimated_current_annual: estimatedCurrentCost,
  our_annual_cost: ourAnnualCost,
  annual_cost_savings: annualSavings,
  time_hours_recovered: timeRecovered,
  time_value_saved: timeValueSaved,
  churn_value_saved: churnValueSaved,
  revenue_gain: revenueGain,
  total_roi_value: totalROIValue,
  roi_multiplier: roiMultiplier,
  deal_size_category: dealSize,
  payback_months: ourAnnualCost > 0 ? Math.ceil(ourAnnualCost / (totalROIValue / 12)) : 0
};`,
          outputVariables: [
            { name: "estimated_current_annual", type: "number" },
            { name: "our_annual_cost", type: "number" },
            { name: "annual_cost_savings", type: "number" },
            { name: "time_hours_recovered", type: "number" },
            { name: "time_value_saved", type: "number" },
            { name: "churn_value_saved", type: "number" },
            { name: "revenue_gain", type: "number" },
            { name: "total_roi_value", type: "number" },
            { name: "roi_multiplier", type: "number" },
            { name: "deal_size_category", type: "string" },
            { name: "payback_months", type: "number" },
          ],
        },
      }),

      // 6. AI ROI narrative
      aiNode(
        "6",
        "AI Generate ROI Narrative",
        1240,
        "You are a value engineering specialist. Given personalized ROI calculations, industry benchmarks, and the prospect context, craft a compelling ROI narrative email that tells the financial story. Lead with the most impactful number, break down savings by category, and include a clear recommendation. Make the ROI feel real and achievable, not theoretical. Respond JSON: {roi_email_subject: string, roi_email_body_html: string, executive_summary: string, key_stat_headline: string, recommendation: string}",
        "Prospect: {prospect_name} at {company_name} ({enriched_industry}, {employee_count} employees). Pain: {primary_pain_point}. Current solution: {current_solution}. ROI: {roi_multiplier}x return. Total value: ${total_roi_value}. Savings: ${annual_cost_savings}/yr. Time recovered: {time_hours_recovered} hrs/yr (${time_value_saved}). Churn savings: ${churn_value_saved}. Revenue gain: ${revenue_gain}. Payback: {payback_months} months. Benchmark source: {benchmark_source} ({data_confidence}). Craft the ROI narrative.",
        [
          {
            field: "roi_email_subject",
            as: "roi_email_subject",
            type: "string",
          },
          {
            field: "roi_email_body_html",
            as: "roi_email_body_html",
            type: "string",
          },
          {
            field: "executive_summary",
            as: "roi_executive_summary",
            type: "string",
          },
          {
            field: "key_stat_headline",
            as: "key_stat_headline",
            type: "string",
          },
          { field: "recommendation", as: "roi_recommendation", type: "string" },
        ],
      ),

      // 7. Branch by deal size
      n("7", "condition", "Enterprise Deal Size?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "deal_size_category",
              operator: "eq",
              value: "enterprise",
            },
          ],
        },
      }),

      // 8. Deliver ROI package via email
      emailNode(
        "8",
        "Deliver ROI Package Email",
        1720,
        "{prospect_email}",
        "{roi_email_subject}",
        "{roi_email_body_html}<p><strong>{key_stat_headline}</strong></p><p>{roi_recommendation}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1878. REDDIT BRAND MENTION MONITOR (9 nodes)
  // ===================================================================

  "Reddit Brand Mention Monitor": {
    summary:
      "Runs on a schedule to search Reddit for brand mentions via API, filters and deduplicates results with runCode, analyzes sentiment and context with AI, branches by sentiment, drafts contextual responses with AI, routes through an approval gate, escalates positive mentions to sales in HubSpot, and alerts the community team via Slack.",
    nodes: [
      trigger("Schedule: Reddit brand mention scan", "schedule", {
        schedule: "0 */4 * * *",
        howItStarts:
          "Runs every 4 hours to scan Reddit for brand mentions — monitors relevant subreddits for discussions about your product, brand, or key use cases to enable timely community engagement and sales opportunity identification",
        setup:
          "Configure Reddit API credentials for search. Set OpenAI API key for sentiment analysis and response drafting. Set HubSpot API token for sales escalation. Configure Slack webhook for community team alerts.",
        inputFields: [],
      }),

      // 2. Search Reddit brand mentions via API
      n("2", "hitApi", "Search Reddit Brand Mentions API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/search?q=YOUR_BRAND_NAME&sort=new&limit=25&t=day",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "BrandMonitor/1.0" },
          ],
          body: [],
          variables: [
            {
              name: "data.children[0].data.title",
              customName: "post_title",
              type: "string",
            },
            {
              name: "data.children[0].data.selftext",
              customName: "post_body",
              type: "string",
            },
            {
              name: "data.children[0].data.subreddit",
              customName: "subreddit",
              type: "string",
            },
            {
              name: "data.children[0].data.author",
              customName: "post_author",
              type: "string",
            },
            {
              name: "data.children[0].data.permalink",
              customName: "post_permalink",
              type: "string",
            },
            {
              name: "data.children[0].data.score",
              customName: "post_score",
              type: "number",
            },
            {
              name: "data.children[0].data.num_comments",
              customName: "comment_count",
              type: "number",
            },
            {
              name: "data.children[0].data.created_utc",
              customName: "post_created_utc",
              type: "number",
            },
          ],
        },
      }),

      // 3. Filter and deduplicate results runCode
      n("3", "runCode", "Filter Deduplicate Reddit Results", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Filter out low-quality mentions and flag high-value posts
const postTitle = (vars.post_title || "").toLowerCase();
const postBody = (vars.post_body || "").toLowerCase();
const subreddit = vars.subreddit || "";
const score = parseInt(vars.post_score) || 0;
const comments = parseInt(vars.comment_count) || 0;
const isQuestion = postTitle.includes("?") || postBody.includes("anyone") || postBody.includes("recommend") || postBody.includes("looking for");
const isBuyingIntent = postBody.includes("pricing") || postBody.includes("alternative") || postBody.includes("switch") || postBody.includes("vs");
const isComplaint = postBody.includes("issue") || postBody.includes("problem") || postBody.includes("bug") || postBody.includes("terrible");
const isPraise = postBody.includes("love") || postBody.includes("amazing") || postBody.includes("recommend") || postBody.includes("great");
const engagementLevel = score > 50 || comments > 20 ? "viral" : score > 10 || comments > 5 ? "active" : "low";
const mentionType = isBuyingIntent ? "buying_intent" : isQuestion ? "question" : isComplaint ? "complaint" : isPraise ? "praise" : "neutral";
const priority = mentionType === "buying_intent" ? "high" : (mentionType === "complaint" && engagementLevel !== "low") ? "high" : mentionType === "praise" ? "medium" : "low";
return {
  mention_type: mentionType,
  engagement_level: engagementLevel,
  mention_priority: priority,
  is_buying_intent: isBuyingIntent ? "true" : "false",
  post_url: "https://reddit.com" + (vars.post_permalink || ""),
  content_preview: (postTitle + " " + postBody).substring(0, 300)
};`,
          outputVariables: [
            { name: "mention_type", type: "string" },
            { name: "engagement_level", type: "string" },
            { name: "mention_priority", type: "string" },
            { name: "is_buying_intent", type: "string" },
            { name: "post_url", type: "string" },
            { name: "content_preview", type: "string" },
          ],
        },
      }),

      // 4. AI analyze sentiment and context
      aiNode(
        "4",
        "AI Analyze Sentiment Context",
        760,
        "You are a social media intelligence analyst specializing in Reddit. Given a brand mention, analyze the sentiment (positive, negative, neutral, mixed), the intent behind the post (seeking recommendation, sharing experience, comparing alternatives, reporting issue, giving praise), and recommend an engagement strategy. Respond JSON: {sentiment: string, intent_analysis: string, engagement_strategy: string, response_urgency: string, sales_opportunity: string, risk_level: string}",
        "Subreddit: r/{subreddit}. Title: {post_title}. Body: {post_body}. Author: {post_author}. Score: {post_score}. Comments: {comment_count}. Mention type: {mention_type}. Engagement: {engagement_level}. Analyze this brand mention.",
        [
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "intent_analysis", as: "intent_analysis", type: "string" },
          {
            field: "engagement_strategy",
            as: "engagement_strategy",
            type: "string",
          },
          { field: "response_urgency", as: "response_urgency", type: "string" },
          {
            field: "sales_opportunity",
            as: "sales_opportunity",
            type: "string",
          },
          { field: "risk_level", as: "risk_level", type: "string" },
        ],
      ),

      // 5. Branch by sentiment
      n("5", "condition", "Positive or Buying Intent?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "sentiment", operator: "in", value: "positive,mixed" },
          ],
        },
      }),

      // 6. AI contextual response draft
      aiNode(
        "6",
        "AI Draft Contextual Response",
        1240,
        "You are a community engagement specialist. Given the Reddit post context, sentiment analysis, and engagement strategy, draft a helpful and authentic Reddit response. Never be salesy — be genuinely helpful, acknowledge the user experience, provide value, and only mention your product naturally if relevant. Follow Reddit community norms. Respond JSON: {response_text: string, tone_guide: string, disclosure_note: string}",
        "Post: {post_title} in r/{subreddit} by u/{post_author}. Body: {post_body}. Sentiment: {sentiment}. Intent: {intent_analysis}. Strategy: {engagement_strategy}. Mention type: {mention_type}. Draft the community response.",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "tone_guide", as: "tone_guide", type: "string" },
          { field: "disclosure_note", as: "disclosure_note", type: "string" },
        ],
      ),

      // 7. Post response approval gate
      n("7", "approvalGate", "Approve Reddit Response", 1480, {
        approvalGateDetails: {
          approvalMessage:
            "Reddit response for r/{subreddit} post by u/{post_author}:\n\nPost: {post_title}\nSentiment: {sentiment} | Type: {mention_type}\n\nProposed response:\n{response_text}\n\nTone: {tone_guide}\nDisclosure: {disclosure_note}",
        },
      }),

      // 8. Escalate positive mentions to sales in HubSpot
      hubspotPostNode(
        "8",
        "Escalate to Sales HubSpot",
        1720,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{post_author}@reddit.placeholder","firstname":"{post_author}","hs_lead_status":"OPEN","notes_last_updated":"Reddit mention: {post_title} in r/{subreddit}. Sentiment: {sentiment}. Sales opp: {sales_opportunity}. URL: {post_url}"}',
          },
        ],
        [{ name: "id", customName: "hs_contact_id", type: "string" }],
      ),

      // 9. Alert community team via Slack
      slackNode(
        "9",
        "Alert Community Team Slack",
        1960,
        "Reddit Mention [{mention_priority}]: *{post_title}*\\nSubreddit: r/{subreddit} | Author: u/{post_author} | Score: {post_score}\\nSentiment: {sentiment} | Type: {mention_type} | Engagement: {engagement_level}\\nIntent: {intent_analysis}\\nStrategy: {engagement_strategy}\\nSales opp: {sales_opportunity}\\nURL: {post_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "9", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1879. REDDIT COMPETITOR MENTION MONITOR (8 nodes)
  // ===================================================================

  "Reddit Competitor Mention Monitor": {
    summary:
      "Runs on a schedule to search Reddit for competitor mentions via API, filters for sales opportunities with runCode, analyzes competitive intelligence with AI, scores opportunity potential with runCode, branches by opportunity score, alerts the sales team via Slack, and logs competitive intelligence to Sheets.",
    nodes: [
      trigger("Schedule: Reddit competitor mention scan", "schedule", {
        schedule: "0 */6 * * *",
        howItStarts:
          "Runs every 6 hours to scan Reddit for competitor mentions — identifies discussions where users are unhappy with competitors, seeking alternatives, or comparing solutions, creating sales opportunities",
        setup:
          "Configure Reddit API credentials for competitor search. Set OpenAI API key for competitive analysis. Configure Slack webhook for sales team alerts. Set Google Sheets credentials for intelligence logging.",
        inputFields: [],
      }),

      // 2. Search Reddit competitor mentions
      n("2", "hitApi", "Search Reddit Competitor Mentions API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/search?q=COMPETITOR_NAME+OR+COMPETITOR2_NAME&sort=new&limit=25&t=day",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "CompetitorMonitor/1.0" },
          ],
          body: [],
          variables: [
            {
              name: "data.children[0].data.title",
              customName: "post_title",
              type: "string",
            },
            {
              name: "data.children[0].data.selftext",
              customName: "post_body",
              type: "string",
            },
            {
              name: "data.children[0].data.subreddit",
              customName: "subreddit",
              type: "string",
            },
            {
              name: "data.children[0].data.author",
              customName: "post_author",
              type: "string",
            },
            {
              name: "data.children[0].data.permalink",
              customName: "post_permalink",
              type: "string",
            },
            {
              name: "data.children[0].data.score",
              customName: "post_score",
              type: "number",
            },
            {
              name: "data.children[0].data.num_comments",
              customName: "comment_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. Filter for sales opportunities runCode
      n("3", "runCode", "Filter Sales Opportunities", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify competitor mentions that represent sales opportunities
const title = (vars.post_title || "").toLowerCase();
const body = (vars.post_body || "").toLowerCase();
const content = title + " " + body;
const isDisplacement = content.includes("leaving") || content.includes("switching") || content.includes("looking for alternative") || content.includes("fed up") || content.includes("canceling") || content.includes("moving away");
const isComparison = content.includes(" vs ") || content.includes("compare") || content.includes("better than") || content.includes("instead of");
const isComplaint = content.includes("hate") || content.includes("terrible") || content.includes("worst") || content.includes("disappointed") || content.includes("overpriced");
const isPriceIssue = content.includes("expensive") || content.includes("pricing") || content.includes("cost too") || content.includes("cheaper");
const score = parseInt(vars.post_score) || 0;
const comments = parseInt(vars.comment_count) || 0;
let opportunityType = "informational";
if (isDisplacement) opportunityType = "displacement";
else if (isComparison) opportunityType = "comparison";
else if (isComplaint) opportunityType = "complaint";
else if (isPriceIssue) opportunityType = "price_sensitivity";
const isSalesOpportunity = isDisplacement || isComparison || isComplaint || isPriceIssue;
const visibility = score > 20 || comments > 10 ? "high" : score > 5 ? "medium" : "low";
return {
  opportunity_type: opportunityType,
  is_sales_opportunity: isSalesOpportunity ? "true" : "false",
  thread_visibility: visibility,
  post_url: "https://reddit.com" + (vars.post_permalink || ""),
  signal_summary: (isDisplacement ? "DISPLACEMENT " : "") + (isComparison ? "COMPARISON " : "") + (isComplaint ? "COMPLAINT " : "") + (isPriceIssue ? "PRICE " : ""),
  content_snippet: content.substring(0, 250)
};`,
          outputVariables: [
            { name: "opportunity_type", type: "string" },
            { name: "is_sales_opportunity", type: "string" },
            { name: "thread_visibility", type: "string" },
            { name: "post_url", type: "string" },
            { name: "signal_summary", type: "string" },
            { name: "content_snippet", type: "string" },
          ],
        },
      }),

      // 4. AI competitive intelligence analysis
      aiNode(
        "4",
        "AI Analyze Competitive Intelligence",
        760,
        "You are a competitive intelligence analyst. Given a Reddit post mentioning a competitor, analyze the competitive dynamics at play. Identify the specific competitor weakness being discussed, what the user values most, potential switching triggers, and how your product could position against this. Respond JSON: {competitor_weakness: string, user_priorities: string, switching_trigger: string, our_positioning: string, battlecard_points: string, outreach_recommendation: string}",
        "Post: {post_title} in r/{subreddit}. Body: {post_body}. Author: u/{post_author}. Opportunity type: {opportunity_type}. Signal: {signal_summary}. Visibility: {thread_visibility}. Score: {post_score}. Comments: {comment_count}. Analyze the competitive intelligence.",
        [
          {
            field: "competitor_weakness",
            as: "competitor_weakness",
            type: "string",
          },
          { field: "user_priorities", as: "user_priorities", type: "string" },
          {
            field: "switching_trigger",
            as: "switching_trigger",
            type: "string",
          },
          { field: "our_positioning", as: "our_positioning", type: "string" },
          {
            field: "battlecard_points",
            as: "battlecard_points",
            type: "string",
          },
          {
            field: "outreach_recommendation",
            as: "outreach_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. Score opportunity potential runCode
      n("5", "runCode", "Score Opportunity Potential", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score the competitive opportunity based on multiple signals
const oppType = (vars.opportunity_type || "").toLowerCase();
const visibility = (vars.thread_visibility || "").toLowerCase();
const isSalesOpp = vars.is_sales_opportunity === "true";
let oppScore = 0;
if (oppType === "displacement") oppScore += 40;
else if (oppType === "comparison") oppScore += 30;
else if (oppType === "complaint") oppScore += 25;
else if (oppType === "price_sensitivity") oppScore += 20;
else oppScore += 5;
if (visibility === "high") oppScore += 30;
else if (visibility === "medium") oppScore += 15;
else oppScore += 5;
if (isSalesOpp) oppScore += 15;
const oppTier = oppScore >= 65 ? "hot_lead" : oppScore >= 40 ? "warm_lead" : "monitor";
const shouldAlert = oppScore >= 40 ? "true" : "false";
return {
  opportunity_score: oppScore,
  opportunity_tier: oppTier,
  should_alert_sales: shouldAlert,
  recommended_action: oppTier === "hot_lead" ? "immediate_outreach" : oppTier === "warm_lead" ? "community_response" : "log_and_monitor"
};`,
          outputVariables: [
            { name: "opportunity_score", type: "number" },
            { name: "opportunity_tier", type: "string" },
            { name: "should_alert_sales", type: "string" },
            { name: "recommended_action", type: "string" },
          ],
        },
      }),

      // 6. Branch by opportunity score
      n("6", "condition", "Sales-Worthy Opportunity?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "should_alert_sales", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Alert sales team via Slack
      slackNode(
        "7",
        "Alert Sales Team Slack",
        1480,
        "Competitive Intel [{opportunity_tier}]: *{post_title}*\\nSubreddit: r/{subreddit} | Score: {opportunity_score}\\nType: {opportunity_type} | Visibility: {thread_visibility}\\nWeakness: {competitor_weakness}\\nUser priorities: {user_priorities}\\nSwitching trigger: {switching_trigger}\\nOur positioning: {our_positioning}\\nBattlecard: {battlecard_points}\\nAction: {recommended_action}\\nURL: {post_url}",
      ),

      // 8. Log competitive intelligence to Sheets
      sheetsNode(
        "8",
        "Log Competitive Intelligence Sheets",
        1720,
        "CompetitiveIntel",
        '[["{post_title}","{subreddit}","{post_author}","{opportunity_type}","{opportunity_score}","{opportunity_tier}","{competitor_weakness}","{switching_trigger}","{our_positioning}","{recommended_action}","{post_url}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1880. REFERRAL PARTNER ACTIVATION (8 nodes)
  // ===================================================================

  "Referral Partner Activation": {
    summary:
      "Runs on a schedule to fetch dormant partner records from HubSpot, analyzes dormancy causes with AI, drafts motivational reactivation emails with AI, pulls relevant success stories with runCode, branches by historical performance, sends reactivation emails, and creates reactivation tasks in HubSpot.",
    nodes: [
      trigger("Schedule: Dormant partner reactivation", "schedule", {
        schedule: "0 9 1 * *",
        howItStarts:
          "Runs monthly to identify and reactivate dormant referral partners — targets partners who have not generated referrals in the past 60+ days with personalized motivation and success stories",
        setup:
          "Configure HubSpot API token for partner record retrieval and task creation. Set OpenAI API key for dormancy analysis and reactivation email drafting. Configure email sending credentials.",
        inputFields: [],
      }),

      // 2. Fetch dormant partner records from HubSpot
      hubspotGetNode(
        "2",
        "Fetch Dormant Partner Records HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].id",
            customName: "partner_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "partner_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "partner_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "partner_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "partner_company",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "total_referrals",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "partner_status",
            type: "string",
          },
        ],
      ),

      // 3. AI analyze dormancy cause
      aiNode(
        "3",
        "AI Analyze Partner Dormancy Cause",
        520,
        "You are a partner success specialist. Given a dormant referral partner profile including their referral history, last contact date, and company details, analyze the likely cause of dormancy. Consider factors like seasonal patterns, relationship cooling, competitive pressures, program friction, or lack of relevant prospects. Respond JSON: {dormancy_cause: string, risk_of_churn: string, reactivation_approach: string, incentive_recommendation: string, relationship_health: string}",
        "Partner: {partner_first_name} {partner_last_name} at {partner_company}. Email: {partner_email}. Total referrals: {total_referrals}. Last contacted: {last_contacted}. Status: {partner_status}. Analyze the dormancy cause.",
        [
          { field: "dormancy_cause", as: "dormancy_cause", type: "string" },
          { field: "risk_of_churn", as: "risk_of_churn", type: "string" },
          {
            field: "reactivation_approach",
            as: "reactivation_approach",
            type: "string",
          },
          {
            field: "incentive_recommendation",
            as: "incentive_recommendation",
            type: "string",
          },
          {
            field: "relationship_health",
            as: "relationship_health",
            type: "string",
          },
        ],
      ),

      // 4. AI motivational reactivation email
      aiNode(
        "4",
        "AI Draft Reactivation Email",
        760,
        "You are a partner relationship manager. Given the dormancy analysis, partner profile, and reactivation approach, craft a warm and motivating reactivation email. Acknowledge the gap without being guilt-inducing. Highlight new developments, success stories from peers, and updated incentives. Make it easy for them to re-engage. Respond JSON: {reactivation_subject: string, reactivation_body_html: string, peer_success_hook: string, new_incentive_offer: string, easy_next_step: string}",
        "Partner: {partner_first_name} {partner_last_name} ({partner_company}). Total referrals: {total_referrals}. Dormancy cause: {dormancy_cause}. Risk: {risk_of_churn}. Approach: {reactivation_approach}. Incentive rec: {incentive_recommendation}. Relationship: {relationship_health}. Craft the reactivation email.",
        [
          {
            field: "reactivation_subject",
            as: "reactivation_subject",
            type: "string",
          },
          {
            field: "reactivation_body_html",
            as: "reactivation_body_html",
            type: "string",
          },
          {
            field: "peer_success_hook",
            as: "peer_success_hook",
            type: "string",
          },
          {
            field: "new_incentive_offer",
            as: "new_incentive_offer",
            type: "string",
          },
          { field: "easy_next_step", as: "easy_next_step", type: "string" },
        ],
      ),

      // 5. Pull relevant success stories runCode
      n("5", "runCode", "Pull Relevant Success Stories", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile relevant peer success stories and program highlights
const totalReferrals = parseInt(vars.total_referrals) || 0;
const partnerCompany = (vars.partner_company || "").toLowerCase();
const historicalPerformance = totalReferrals >= 10 ? "high_performer" : totalReferrals >= 3 ? "moderate_performer" : "low_performer";
const successStory = historicalPerformance === "high_performer"
  ? "Partners like you have earned $" + (totalReferrals * 500) + "+ in commissions. Your network continues to grow and we have new tools to help."
  : historicalPerformance === "moderate_performer"
  ? "Similar partners who re-engaged saw a 3x increase in referral conversions with our updated program materials and higher commission tiers."
  : "New partners are seeing their first referral close within 30 days using our updated pitch deck and co-marketing materials.";
const updatedProgramHighlights = [
  "Increased commission rates (up to 25%)",
  "New co-branded marketing materials",
  "Dedicated partner success manager",
  "Real-time referral dashboard"
];
return {
  historical_performance: historicalPerformance,
  success_story: successStory,
  program_highlights: updatedProgramHighlights.join("; "),
  estimated_earnings: "$" + (totalReferrals * 500),
  reactivation_urgency: historicalPerformance === "high_performer" ? "high" : "medium"
};`,
          outputVariables: [
            { name: "historical_performance", type: "string" },
            { name: "success_story", type: "string" },
            { name: "program_highlights", type: "string" },
            { name: "estimated_earnings", type: "string" },
            { name: "reactivation_urgency", type: "string" },
          ],
        },
      }),

      // 6. Branch by historical performance
      n("6", "condition", "High Historical Performer?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "historical_performance",
              operator: "eq",
              value: "high_performer",
            },
          ],
        },
      }),

      // 7. Send reactivation email
      emailNode(
        "7",
        "Send Partner Reactivation Email",
        1480,
        "{partner_email}",
        "{reactivation_subject}",
        "{reactivation_body_html}<p>{success_story}</p><p>Program updates: {program_highlights}</p><p>{new_incentive_offer}</p><p>{easy_next_step}</p>",
      ),

      // 8. Create reactivation tasks in HubSpot
      hubspotPostNode(
        "8",
        "Create Reactivation Tasks HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Follow up on partner reactivation: {partner_first_name} {partner_last_name}","hs_task_body":"Dormancy cause: {dormancy_cause}. Risk: {risk_of_churn}. Approach: {reactivation_approach}. Performance: {historical_performance}. Email sent with: {new_incentive_offer}","hs_task_priority":"HIGH","hubspot_owner_id":"{csm_owner_id}","hs_task_status":"NOT_STARTED"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1881. RENEWAL STAKEHOLDER MAPPING (9 nodes)
  // ===================================================================

  "Renewal Stakeholder Mapping": {
    summary:
      "Runs on a schedule 90 days before renewal, fetches account contract details from HubSpot, discovers current decision makers via API, detects stakeholder changes with runCode, assesses renewal risk with runCode, generates a retention engagement plan with AI, branches by renewal risk, creates stakeholder engagement tasks in HubSpot, and alerts the renewal team via Slack.",
    nodes: [
      trigger("Schedule: Renewal mapping 90 days before", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs weekly to identify accounts approaching contract renewal within 90 days — maps the current stakeholder landscape, detects changes since initial sale, and proactively builds a retention strategy",
        setup:
          "Configure HubSpot API token for contract and contact data. Set Hunter.io API key for stakeholder discovery. Set OpenAI API key for retention planning. Configure Slack webhook for renewal team alerts.",
        inputFields: [],
      }),

      // 2. Fetch account contract details from HubSpot
      hubspotGetNode(
        "2",
        "Fetch Account Contract Details HubSpot",
        280,
        "crm/v3/objects/deals?limit=1&properties=dealname,amount,closedate,hubspot_owner_id,pipeline,dealstage,hs_next_step",
        [
          { name: "results[0].id", customName: "deal_id", type: "string" },
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "contract_value",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "renewal_date",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "account_owner_id",
            type: "string",
          },
          {
            name: "results[0].properties.pipeline",
            customName: "pipeline",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_next_step",
            customName: "next_step",
            type: "string",
          },
        ],
      ),

      // 3. Discover current decision makers via Hunter.io
      n("3", "hitApi", "Discover Current Decision Makers", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={deal_name}&api_key={hunter_api_key}&department=executive&limit=10",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "dm_email_1",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "dm_first_1",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "dm_last_1",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "dm_title_1",
              type: "string",
            },
            {
              name: "data.emails[1].value",
              customName: "dm_email_2",
              type: "string",
            },
            {
              name: "data.emails[1].position",
              customName: "dm_title_2",
              type: "string",
            },
            {
              name: "meta.results",
              customName: "total_dms_found",
              type: "number",
            },
          ],
        },
      }),

      // 4. Detect stakeholder changes runCode
      n("4", "runCode", "Detect Stakeholder Changes", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze stakeholder landscape for renewal risk factors
const dmCount = parseInt(vars.total_dms_found) || 0;
const contractValue = parseFloat(vars.contract_value) || 0;
const renewalDate = new Date(vars.renewal_date || Date.now());
const daysToRenewal = Math.floor((renewalDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
const dmTitle1 = (vars.dm_title_1 || "").toLowerCase();
const hasCLevel = dmTitle1.includes("chief") || dmTitle1.includes("ceo") || dmTitle1.includes("vp");
const hasMultipleContacts = dmCount >= 3;
const stakeholderHealth = hasMultipleContacts && hasCLevel ? "strong" : hasMultipleContacts ? "moderate" : hasCLevel ? "single_thread_risk" : "weak";
const changeRisk = stakeholderHealth === "weak" ? "high" : stakeholderHealth === "single_thread_risk" ? "high" : "low";
return {
  days_to_renewal: daysToRenewal,
  stakeholder_health: stakeholderHealth,
  change_risk: changeRisk,
  dm_count: dmCount,
  has_executive_contact: hasCLevel ? "true" : "false",
  multi_threaded: hasMultipleContacts ? "true" : "false",
  renewal_urgency: daysToRenewal <= 30 ? "critical" : daysToRenewal <= 60 ? "high" : "standard"
};`,
          outputVariables: [
            { name: "days_to_renewal", type: "number" },
            { name: "stakeholder_health", type: "string" },
            { name: "change_risk", type: "string" },
            { name: "dm_count", type: "number" },
            { name: "has_executive_contact", type: "string" },
            { name: "multi_threaded", type: "string" },
            { name: "renewal_urgency", type: "string" },
          ],
        },
      }),

      // 5. Assess renewal risk runCode
      n("5", "runCode", "Assess Renewal Risk Score", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate comprehensive renewal risk score
const daysToRenewal = parseInt(vars.days_to_renewal) || 90;
const contractValue = parseFloat(vars.contract_value) || 0;
const stakeholderHealth = vars.stakeholder_health || "moderate";
const changeRisk = vars.change_risk || "low";
let riskScore = 0;
if (stakeholderHealth === "weak") riskScore += 35;
else if (stakeholderHealth === "single_thread_risk") riskScore += 25;
else if (stakeholderHealth === "moderate") riskScore += 10;
if (changeRisk === "high") riskScore += 25;
else if (changeRisk === "medium") riskScore += 15;
if (daysToRenewal <= 30) riskScore += 20;
else if (daysToRenewal <= 60) riskScore += 10;
const renewalRisk = riskScore >= 60 ? "high" : riskScore >= 35 ? "medium" : "low";
const strategy = renewalRisk === "high" ? "executive_engagement_blitz" : renewalRisk === "medium" ? "proactive_value_reinforcement" : "standard_renewal_process";
return {
  renewal_risk_score: riskScore,
  renewal_risk_level: renewalRisk,
  retention_strategy: strategy,
  is_at_risk: renewalRisk === "high" ? "true" : "false",
  contract_tier: contractValue > 100000 ? "strategic" : contractValue > 25000 ? "key" : "standard"
};`,
          outputVariables: [
            { name: "renewal_risk_score", type: "number" },
            { name: "renewal_risk_level", type: "string" },
            { name: "retention_strategy", type: "string" },
            { name: "is_at_risk", type: "string" },
            { name: "contract_tier", type: "string" },
          ],
        },
      }),

      // 6. AI retention engagement plan
      aiNode(
        "6",
        "AI Generate Retention Engagement Plan",
        1240,
        "You are a customer retention strategist. Given the renewal risk assessment, stakeholder mapping, and contract details, create a detailed engagement plan to secure the renewal. For high-risk accounts, recommend executive-level interventions. For medium-risk, suggest value reinforcement activities. Include specific tasks, timelines, and messaging for each stakeholder. Respond JSON: {engagement_plan: string, executive_action: string, value_messaging: string, stakeholder_tasks: string, timeline: string, escalation_criteria: string}",
        "Account: {deal_name} (${contract_value}, {contract_tier}). Renewal: {days_to_renewal} days. Risk: {renewal_risk_level} (score {renewal_risk_score}). Stakeholder health: {stakeholder_health}. DMs found: {dm_count}. Executive contact: {has_executive_contact}. Multi-threaded: {multi_threaded}. Strategy: {retention_strategy}. Primary DM: {dm_first_1} {dm_last_1} ({dm_title_1}). Build the retention plan.",
        [
          { field: "engagement_plan", as: "engagement_plan", type: "string" },
          { field: "executive_action", as: "executive_action", type: "string" },
          { field: "value_messaging", as: "value_messaging", type: "string" },
          {
            field: "stakeholder_tasks",
            as: "stakeholder_tasks",
            type: "string",
          },
          { field: "timeline", as: "engagement_timeline", type: "string" },
          {
            field: "escalation_criteria",
            as: "escalation_criteria",
            type: "string",
          },
        ],
      ),

      // 7. Branch by renewal risk
      n("7", "condition", "High Renewal Risk?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "is_at_risk", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8. Create stakeholder engagement tasks in HubSpot
      hubspotPostNode(
        "8",
        "Create Engagement Tasks HubSpot",
        1720,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Renewal retention: {deal_name} ({renewal_risk_level} risk)","hs_task_body":"Risk score: {renewal_risk_score}. Strategy: {retention_strategy}. Plan: {engagement_plan}. Executive action: {executive_action}. Stakeholder tasks: {stakeholder_tasks}. Timeline: {engagement_timeline}","hs_task_priority":"HIGH","hubspot_owner_id":"{account_owner_id}","hs_task_status":"NOT_STARTED"}',
          },
        ],
        [
          { name: "id", customName: "task_id", type: "string" },
          {
            name: "properties.hs_task_subject",
            customName: "task_subject",
            type: "string",
          },
        ],
      ),

      // 9. Alert renewal team via Slack
      slackNode(
        "9",
        "Alert Renewal Team Slack",
        1960,
        "Renewal Alert [{renewal_risk_level}]: *{deal_name}* (${contract_value})\\nDays to renewal: {days_to_renewal} | Risk score: {renewal_risk_score}\\nStakeholder health: {stakeholder_health} | DMs: {dm_count}\\nStrategy: {retention_strategy}\\nPlan: {engagement_plan}\\nExecutive action: {executive_action}\\nEscalation: {escalation_criteria}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1882. REPEAT PRICING PAGE VISITOR ALERT (8 nodes)
  // ===================================================================

  "Repeat Pricing Page Visitor Alert": {
    summary:
      "Fires when a pricing page visit signal arrives via webhook, extracts visitor data with AI, identifies the visitor via Clearbit, pulls visit history from HubSpot CRM, scores purchase intent with runCode, branches by intent score, generates a contextual talk track with AI, alerts the sales rep via Slack, and creates an urgent follow-up task in HubSpot.",
    nodes: [
      trigger("Webhook: Pricing page repeat visit", "webhook_app", {
        source: "website_analytics",
        event: "page.pricing_repeat_visit",
        howItStarts:
          "Fires when a visitor returns to the pricing page for the 3rd+ time — a strong buying signal that indicates active evaluation and comparison shopping, warranting immediate sales outreach",
        setup:
          "Configure website analytics webhook for repeat pricing page visits. Set Clearbit API key for visitor identification. Set HubSpot API token for visit history and task creation. Set OpenAI API key for talk track generation. Configure Slack webhook for sales alerts.",
        inputFields: [],
      }),

      // 2. AI extract visitor data
      aiNode(
        "2",
        "AI Extract Pricing Visitor Data",
        280,
        "You are a website analytics parser. Extract the visitor email or IP address, visitor name if available, company name or domain, page URL visited, visit count, referral source, time on page seconds, pages viewed in session, UTM parameters if present, and the device type from the raw webhook payload. Respond JSON: {visitor_email: string, visitor_name: string, company_domain: string, page_url: string, visit_count: string, referral_source: string, time_on_page: string, pages_viewed: string, utm_source: string, utm_campaign: string, device_type: string}",
        "Raw webhook payload: {webhook_payload}. Extract all pricing page visitor data regardless of field naming conventions.",
        [
          { field: "visitor_email", as: "visitor_email", type: "string" },
          { field: "visitor_name", as: "visitor_name", type: "string" },
          { field: "company_domain", as: "company_domain", type: "string" },
          { field: "page_url", as: "page_url", type: "string" },
          { field: "visit_count", as: "visit_count", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
          { field: "time_on_page", as: "time_on_page", type: "string" },
          { field: "pages_viewed", as: "pages_viewed", type: "string" },
          { field: "utm_source", as: "utm_source", type: "string" },
          { field: "utm_campaign", as: "utm_campaign", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
        ],
      ),

      // 3. Identify visitor via Clearbit
      n("3", "hitApi", "Identify Visitor Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_domain}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "identified_company", type: "string" },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            { name: "tech", customName: "company_tech_stack", type: "string" },
          ],
        },
      }),

      // 4. Pull visit history from CRM HubSpot
      hubspotGetNode(
        "4",
        "Pull Visit History CRM HubSpot",
        760,
        "crm/v3/objects/contacts/search?query={visitor_email}",
        [
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "last_name",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "total_page_views",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_visits",
            customName: "total_site_visits",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "assigned_rep_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "lead_status",
            type: "string",
          },
        ],
      ),

      // 5. Score purchase intent runCode
      n("5", "runCode", "Score Purchase Intent", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate purchase intent score from multiple signals
const visitCount = parseInt(vars.visit_count) || 1;
const timeOnPage = parseInt(vars.time_on_page) || 0;
const totalVisits = parseInt(vars.total_site_visits) || visitCount;
const employees = parseInt(vars.company_employees) || 0;
const pagesViewed = parseInt(vars.pages_viewed) || 1;
let intentScore = 0;
if (visitCount >= 5) intentScore += 35;
else if (visitCount >= 3) intentScore += 25;
else intentScore += 10;
if (timeOnPage > 120) intentScore += 20;
else if (timeOnPage > 60) intentScore += 15;
else intentScore += 5;
if (pagesViewed > 5) intentScore += 15;
else if (pagesViewed > 2) intentScore += 10;
if (employees > 100) intentScore += 15;
else if (employees > 20) intentScore += 10;
else intentScore += 5;
const intentLevel = intentScore >= 70 ? "very_high" : intentScore >= 50 ? "high" : intentScore >= 30 ? "medium" : "low";
const shouldAlert = intentScore >= 45 ? "true" : "false";
return {
  intent_score: intentScore,
  intent_level: intentLevel,
  should_alert_rep: shouldAlert,
  urgency: intentScore >= 70 ? "immediate" : intentScore >= 50 ? "within_1_hour" : "within_4_hours",
  deal_potential: employees > 200 ? "enterprise" : employees > 50 ? "mid_market" : "smb"
};`,
          outputVariables: [
            { name: "intent_score", type: "number" },
            { name: "intent_level", type: "string" },
            { name: "should_alert_rep", type: "string" },
            { name: "urgency", type: "string" },
            { name: "deal_potential", type: "string" },
          ],
        },
      }),

      // 6. Branch by intent score
      n("6", "condition", "High Purchase Intent?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "should_alert_rep", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. AI contextual talk track
      aiNode(
        "7",
        "AI Generate Contextual Talk Track",
        1480,
        "You are a sales enablement specialist. Given a repeat pricing page visitor profile with company enrichment and intent signals, generate a contextual talk track for the sales rep. Include a personalized opening line, key talking points based on their company size and industry, common objections to prepare for, and a recommended pricing approach. Respond JSON: {opening_line: string, talking_points: string, objection_prep: string, pricing_approach: string, recommended_plan: string}",
        "Visitor: {visitor_name} ({first_name} {last_name}) from {identified_company} ({company_industry}, {company_employees} employees). Visit count: {visit_count}. Time on pricing: {time_on_page}s. Pages: {pages_viewed}. Intent: {intent_level} ({intent_score}). Revenue: {company_revenue}. Tech stack: {company_tech_stack}. Lead status: {lead_status}. UTM: {utm_source}/{utm_campaign}. Generate the talk track.",
        [
          { field: "opening_line", as: "opening_line", type: "string" },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "objection_prep", as: "objection_prep", type: "string" },
          { field: "pricing_approach", as: "pricing_approach", type: "string" },
          { field: "recommended_plan", as: "recommended_plan", type: "string" },
        ],
      ),

      // 8. Alert sales rep via Slack + create task
      slackNode(
        "8",
        "Alert Sales Rep Slack",
        1720,
        "[{urgency}] Pricing Page Alert: *{identified_company}* ({intent_level} intent, score {intent_score})\\nVisitor: {visitor_name} ({visitor_email}) — Visit #{visit_count}\\nCompany: {company_employees} employees, {company_industry}, {company_revenue} revenue\\nDeal potential: {deal_potential} | Plan rec: {recommended_plan}\\n\\nTalk Track:\\nOpening: {opening_line}\\nPoints: {talking_points}\\nObjections: {objection_prep}\\nPricing: {pricing_approach}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1883. RESEARCH COLLABORATION OUTREACH (8 nodes)
  // ===================================================================

  "Research Collaboration Outreach": {
    summary:
      "Starts from a manual trigger with research campaign details, searches academic publications via API, analyzes research alignment with AI, fetches academic contacts via API, drafts personalized collaboration proposals with AI, branches by academic seniority, sends research pitch emails, and logs outreach to Sheets.",
    nodes: [
      trigger("Manual Trigger: Research collaboration campaign", "manual", {
        howItStarts:
          "Triggered manually when the research partnerships team is ready to launch an academic collaboration outreach campaign — includes research area, desired collaboration type, and target institution criteria",
        setup:
          "Configure Semantic Scholar or CrossRef API key for publication search. Set OpenAI API key for alignment analysis and proposal drafting. Configure email sending credentials. Set Google Sheets credentials for outreach tracking.",
        inputFields: [
          {
            name: "research_topic",
            type: "string",
            example: "AI-driven customer engagement optimization",
          },
          {
            name: "collaboration_type",
            type: "string",
            example: "joint_paper, data_sharing, advisory",
          },
          {
            name: "target_institution_type",
            type: "string",
            example: "R1 university, research lab",
          },
          {
            name: "our_data_offering",
            type: "string",
            example: "anonymized engagement data, 10M+ customer interactions",
          },
          {
            name: "budget_for_collaboration",
            type: "string",
            example: "$25,000-50,000",
          },
          {
            name: "campaign_owner_email",
            type: "string",
            example: "research@company.com",
          },
        ],
      }),

      // 2. Search academic publications via Semantic Scholar API
      n("2", "hitApi", "Search Academic Publications API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.semanticscholar.org/graph/v1/paper/search?query={research_topic}&limit=10&fields=title,authors,year,citationCount,abstract,externalIds",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data[0].title",
              customName: "paper_title",
              type: "string",
            },
            {
              name: "data[0].abstract",
              customName: "paper_abstract",
              type: "string",
            },
            { name: "data[0].year", customName: "paper_year", type: "number" },
            {
              name: "data[0].citationCount",
              customName: "citation_count",
              type: "number",
            },
            {
              name: "data[0].authors[0].name",
              customName: "author_name",
              type: "string",
            },
            {
              name: "data[0].authors[0].authorId",
              customName: "author_id",
              type: "string",
            },
            { name: "total", customName: "total_papers_found", type: "number" },
          ],
        },
      }),

      // 3. AI analyze research alignment
      aiNode(
        "3",
        "AI Analyze Research Alignment",
        520,
        "You are a research partnerships analyst. Given an academic paper, its author, and your company research interests, analyze the alignment between the academic research and your collaboration goals. Assess the quality of the researcher, relevance of their work, and potential collaboration angles. Respond JSON: {alignment_score: string, relevance_analysis: string, collaboration_angles: string, researcher_quality: string, seniority_estimate: string, institution_type: string}",
        "Our topic: {research_topic}. Collaboration type: {collaboration_type}. Our data offering: {our_data_offering}. Paper: {paper_title} ({paper_year}). Abstract: {paper_abstract}. Author: {author_name}. Citations: {citation_count}. Analyze the research alignment.",
        [
          { field: "alignment_score", as: "alignment_score", type: "string" },
          {
            field: "relevance_analysis",
            as: "relevance_analysis",
            type: "string",
          },
          {
            field: "collaboration_angles",
            as: "collaboration_angles",
            type: "string",
          },
          {
            field: "researcher_quality",
            as: "researcher_quality",
            type: "string",
          },
          {
            field: "seniority_estimate",
            as: "seniority_estimate",
            type: "string",
          },
          { field: "institution_type", as: "institution_type", type: "string" },
        ],
      ),

      // 4. Fetch academic contact details via API
      n("4", "hitApi", "Fetch Academic Contact Details", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.semanticscholar.org/graph/v1/author/{author_id}?fields=name,affiliations,homepage,paperCount,citationCount,hIndex",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "name", customName: "full_author_name", type: "string" },
            {
              name: "affiliations[0]",
              customName: "author_affiliation",
              type: "string",
            },
            { name: "homepage", customName: "author_homepage", type: "string" },
            {
              name: "paperCount",
              customName: "author_paper_count",
              type: "number",
            },
            {
              name: "citationCount",
              customName: "author_total_citations",
              type: "number",
            },
            { name: "hIndex", customName: "author_h_index", type: "number" },
          ],
        },
      }),

      // 5. AI personalized collaboration proposal
      aiNode(
        "5",
        "AI Draft Collaboration Proposal",
        1000,
        "You are a research collaboration specialist at a tech company. Given an academic researcher profile, their publication history, and your company collaboration goals, craft a respectful and compelling collaboration proposal email. Academics value intellectual merit, data access, and publication opportunities over commercial benefits. Tailor the tone to their seniority level. Respond JSON: {proposal_subject: string, proposal_body_html: string, value_to_researcher: string, data_access_offer: string, next_step_suggestion: string}",
        "Researcher: {full_author_name} at {author_affiliation}. H-index: {author_h_index}. Papers: {author_paper_count}. Citations: {author_total_citations}. Recent paper: {paper_title}. Seniority: {seniority_estimate}. Alignment: {alignment_score}. Angles: {collaboration_angles}. Our topic: {research_topic}. Collab type: {collaboration_type}. Data: {our_data_offering}. Budget: {budget_for_collaboration}. Draft the proposal.",
        [
          { field: "proposal_subject", as: "proposal_subject", type: "string" },
          {
            field: "proposal_body_html",
            as: "proposal_body_html",
            type: "string",
          },
          {
            field: "value_to_researcher",
            as: "value_to_researcher",
            type: "string",
          },
          {
            field: "data_access_offer",
            as: "data_access_offer",
            type: "string",
          },
          {
            field: "next_step_suggestion",
            as: "next_step_suggestion",
            type: "string",
          },
        ],
      ),

      // 6. Branch by academic seniority
      n("6", "condition", "Senior Researcher (H-index > 20)?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "author_h_index", operator: "gte", value: "20" },
          ],
        },
      }),

      // 7. Send research pitch email
      emailNode(
        "7",
        "Send Research Collaboration Pitch",
        1480,
        "{author_homepage}",
        "{proposal_subject}",
        "{proposal_body_html}<p>What we offer: {data_access_offer}</p><p>{next_step_suggestion}</p>",
      ),

      // 8. Log research outreach to Sheets
      sheetsNode(
        "8",
        "Log Research Outreach Sheets",
        1720,
        "ResearchOutreach",
        '[["{full_author_name}","{author_affiliation}","{author_h_index}","{paper_title}","{alignment_score}","{seniority_estimate}","{collaboration_angles}","{proposal_subject}","sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1884. RESOURCE PAGE LINK OUTREACH (8 nodes)
  // ===================================================================

  "Resource Page Link Outreach": {
    summary:
      "Starts from a manual trigger with link campaign details, crawls resource page content with runCode, analyzes page-content fit with AI, discovers the page curator contact via API, drafts a personalized link request with AI, branches by page authority, sends link outreach emails, and tracks outreach in an SEO Sheets tracker.",
    nodes: [
      trigger("Manual Trigger: Link outreach campaign", "manual", {
        howItStarts:
          "Triggered manually when the SEO team identifies resource pages that would be ideal for link inclusion — includes the target resource page URL, content to promote, and outreach criteria",
        setup:
          "Configure OpenAI API key for content analysis and outreach drafting. Set Hunter.io API key for curator contact discovery. Configure email sending credentials. Set Google Sheets credentials for SEO outreach tracking.",
        inputFields: [
          {
            name: "resource_page_url",
            type: "string",
            example: "https://example.com/best-marketing-tools-2026",
          },
          {
            name: "resource_page_domain",
            type: "string",
            example: "example.com",
          },
          {
            name: "our_content_url",
            type: "string",
            example:
              "https://company.com/guides/email-automation-best-practices",
          },
          {
            name: "our_content_title",
            type: "string",
            example: "Complete Guide to Email Automation Best Practices",
          },
          {
            name: "content_description",
            type: "string",
            example:
              "Comprehensive 5000-word guide with original research, benchmarks, and templates",
          },
          {
            name: "target_anchor_text",
            type: "string",
            example: "email automation best practices",
          },
        ],
      }),

      // 2. Analyze resource page content with runCode
      n("2", "runCode", "Crawl Resource Page Content", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and analyze the resource page structure
const pageUrl = vars.resource_page_url || "";
const pageDomain = vars.resource_page_domain || "";
const ourContent = vars.our_content_title || "";
const contentDesc = vars.content_description || "";
const domainParts = pageDomain.split(".");
const isHighAuthority = domainParts.length >= 2;
const pageType = pageUrl.includes("best") || pageUrl.includes("top") ? "listicle" : pageUrl.includes("resource") || pageUrl.includes("tools") ? "resource_page" : pageUrl.includes("guide") ? "guide" : "general";
const outreachId = "LINK-" + Date.now().toString(36).toUpperCase();
const estimatedDA = isHighAuthority ? "medium_to_high" : "low_to_medium";
return {
  page_type: pageType,
  outreach_id: outreachId,
  estimated_authority: estimatedDA,
  target_domain: pageDomain,
  content_fit_keywords: contentDesc.split(" ").filter(w => w.length > 5).slice(0, 5).join(", "),
  link_value_estimate: pageType === "listicle" ? "high" : pageType === "resource_page" ? "high" : "medium"
};`,
          outputVariables: [
            { name: "page_type", type: "string" },
            { name: "outreach_id", type: "string" },
            { name: "estimated_authority", type: "string" },
            { name: "target_domain", type: "string" },
            { name: "content_fit_keywords", type: "string" },
            { name: "link_value_estimate", type: "string" },
          ],
        },
      }),

      // 3. AI analyze page-content fit
      aiNode(
        "3",
        "AI Analyze Page-Content Fit",
        520,
        "You are an SEO link building specialist. Given a resource page type and description, and the content being promoted, analyze how well the content fits the page. Determine the best positioning angle, why the page curator would want to include it, and what unique value it adds over existing links on the page. Respond JSON: {fit_score: string, positioning_angle: string, unique_value_add: string, curator_benefit: string, rejection_risk: string}",
        "Resource page: {resource_page_url} (type: {page_type}, authority: {estimated_authority}). Domain: {target_domain}. Our content: {our_content_title} — {content_description}. Target anchor: {target_anchor_text}. Keywords: {content_fit_keywords}. Analyze the page-content fit.",
        [
          { field: "fit_score", as: "fit_score", type: "string" },
          {
            field: "positioning_angle",
            as: "positioning_angle",
            type: "string",
          },
          { field: "unique_value_add", as: "unique_value_add", type: "string" },
          { field: "curator_benefit", as: "curator_benefit", type: "string" },
          { field: "rejection_risk", as: "rejection_risk", type: "string" },
        ],
      ),

      // 4. Discover page curator contact via Hunter.io
      n("4", "hitApi", "Discover Page Curator Contact", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hunter.io/v2/domain-search?domain={resource_page_domain}&api_key={hunter_api_key}&department=marketing&limit=5",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "data.emails[0].value",
              customName: "curator_email",
              type: "string",
            },
            {
              name: "data.emails[0].first_name",
              customName: "curator_first_name",
              type: "string",
            },
            {
              name: "data.emails[0].last_name",
              customName: "curator_last_name",
              type: "string",
            },
            {
              name: "data.emails[0].position",
              customName: "curator_position",
              type: "string",
            },
            {
              name: "data.emails[0].confidence",
              customName: "email_confidence",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI personalized link request
      aiNode(
        "5",
        "AI Draft Personalized Link Request",
        1000,
        "You are a link building outreach specialist. Given the page curator details, content fit analysis, and the resource being promoted, craft a personalized and non-spammy link inclusion request. Be genuine, compliment the page, explain the specific value your content would add to their readers, and make it easy to say yes. Respond JSON: {link_subject: string, link_body_html: string, compliment_hook: string, value_proposition: string, easy_add_instructions: string}",
        "Curator: {curator_first_name} {curator_last_name} ({curator_position}) at {target_domain}. Resource page: {resource_page_url} ({page_type}). Our content: {our_content_title} ({our_content_url}). Fit: {fit_score}. Angle: {positioning_angle}. Unique value: {unique_value_add}. Curator benefit: {curator_benefit}. Anchor text: {target_anchor_text}. Draft the link request.",
        [
          { field: "link_subject", as: "link_subject", type: "string" },
          { field: "link_body_html", as: "link_body_html", type: "string" },
          { field: "compliment_hook", as: "compliment_hook", type: "string" },
          {
            field: "value_proposition",
            as: "link_value_proposition",
            type: "string",
          },
          {
            field: "easy_add_instructions",
            as: "easy_add_instructions",
            type: "string",
          },
        ],
      ),

      // 6. Branch by page authority
      n("6", "condition", "High Authority Page?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "link_value_estimate", operator: "eq", value: "high" },
          ],
        },
      }),

      // 7. Send link outreach email
      emailNode(
        "7",
        "Send Link Outreach Email",
        1480,
        "{curator_email}",
        "{link_subject}",
        "{link_body_html}<p>{easy_add_instructions}</p>",
      ),

      // 8. Track outreach in SEO Sheets
      sheetsNode(
        "8",
        "Track Outreach SEO Sheets",
        1720,
        "SEOLinkOutreach",
        '[["{outreach_id}","{target_domain}","{resource_page_url}","{page_type}","{fit_score}","{link_value_estimate}","{curator_email}","{curator_first_name} {curator_last_name}","{our_content_url}","{link_subject}","sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1885. RETAIL SEASONAL PROMOTIONS OUTREACH (9 nodes)
  // ===================================================================

  "Retail Seasonal Promotions Outreach": {
    summary:
      "Starts from a manual trigger with seasonal campaign details, fetches the retail prospect list from HubSpot, researches seasonal trends with AI, segments by retail vertical with runCode, drafts season-specific pitches with AI, branches by customer vs prospect status, validates email addresses via API, queues timed email delivery with a delay, and tracks campaign performance in Sheets.",
    nodes: [
      trigger("Manual Trigger: Seasonal promotions campaign", "manual", {
        howItStarts:
          "Triggered manually when the sales team launches a seasonal promotions outreach campaign targeting retail prospects — timed to align with major retail seasons (holiday, back-to-school, spring, summer)",
        setup:
          "Configure HubSpot API token for prospect list retrieval. Set OpenAI API key for trend research and pitch generation. Set ZeroBounce API key for email validation. Configure email sending credentials. Set Google Sheets credentials for performance tracking.",
        inputFields: [
          { name: "season_name", type: "string", example: "Holiday 2026" },
          { name: "season_start_date", type: "string", example: "2026-11-01" },
          {
            name: "promotion_theme",
            type: "string",
            example: "Black Friday / Cyber Monday engagement automation",
          },
          {
            name: "offer_details",
            type: "string",
            example:
              "25% off annual plans, free onboarding, holiday campaign templates",
          },
          {
            name: "target_vertical",
            type: "string",
            example: "ecommerce,fashion,electronics,home_goods",
          },
          {
            name: "campaign_owner",
            type: "string",
            example: "retail-team@company.com",
          },
        ],
      }),

      // 2. Fetch retail prospect list from HubSpot
      hubspotGetNode(
        "2",
        "Fetch Retail Prospect List HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          { name: "results[0].id", customName: "prospect_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "prospect_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "prospect_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "prospect_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "prospect_company",
            type: "string",
          },
          {
            name: "results[0].properties.industry",
            customName: "prospect_industry",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "prospect_status",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          { name: "total", customName: "total_prospects", type: "number" },
        ],
      ),

      // 3. AI research seasonal trends
      aiNode(
        "3",
        "AI Research Seasonal Trends",
        520,
        "You are a retail marketing analyst. Given the upcoming retail season and target verticals, research key seasonal trends, consumer behavior shifts, and marketing challenges retailers face during this period. Provide specific statistics and insights that would make a compelling pitch angle. Respond JSON: {seasonal_trend_1: string, seasonal_trend_2: string, consumer_behavior_shift: string, marketing_challenge: string, our_solution_angle: string, urgency_factor: string}",
        "Season: {season_name} starting {season_start_date}. Theme: {promotion_theme}. Verticals: {target_vertical}. Prospect industry: {prospect_industry}. Company: {prospect_company}. Research the seasonal trends.",
        [
          { field: "seasonal_trend_1", as: "seasonal_trend_1", type: "string" },
          { field: "seasonal_trend_2", as: "seasonal_trend_2", type: "string" },
          {
            field: "consumer_behavior_shift",
            as: "consumer_behavior_shift",
            type: "string",
          },
          {
            field: "marketing_challenge",
            as: "marketing_challenge",
            type: "string",
          },
          {
            field: "our_solution_angle",
            as: "our_solution_angle",
            type: "string",
          },
          { field: "urgency_factor", as: "urgency_factor", type: "string" },
        ],
      ),

      // 4. Segment by retail vertical runCode
      n("4", "runCode", "Segment by Retail Vertical", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment prospect by retail vertical and customize approach
const industry = (vars.prospect_industry || "").toLowerCase();
const company = (vars.prospect_company || "").toLowerCase();
const status = (vars.prospect_status || "").toLowerCase();
const lifecycle = (vars.lifecycle_stage || "").toLowerCase();
let vertical = "general_retail";
if (industry.includes("ecommerce") || industry.includes("e-commerce") || company.includes("shop")) vertical = "ecommerce";
else if (industry.includes("fashion") || industry.includes("apparel") || industry.includes("clothing")) vertical = "fashion";
else if (industry.includes("electronics") || industry.includes("tech")) vertical = "electronics";
else if (industry.includes("home") || industry.includes("furniture") || industry.includes("decor")) vertical = "home_goods";
else if (industry.includes("food") || industry.includes("grocery") || industry.includes("beverage")) vertical = "food_beverage";
const isExistingCustomer = lifecycle === "customer" || status === "customer";
const pitchIntensity = isExistingCustomer ? "upsell" : status === "open" || status === "in_progress" ? "warm" : "cold";
const seasonalRelevance = vertical === "ecommerce" ? "very_high" : vertical === "fashion" ? "high" : "medium";
return {
  retail_vertical: vertical,
  is_existing_customer: isExistingCustomer ? "true" : "false",
  pitch_intensity: pitchIntensity,
  seasonal_relevance: seasonalRelevance,
  segment_label: vertical + "_" + pitchIntensity
};`,
          outputVariables: [
            { name: "retail_vertical", type: "string" },
            { name: "is_existing_customer", type: "string" },
            { name: "pitch_intensity", type: "string" },
            { name: "seasonal_relevance", type: "string" },
            { name: "segment_label", type: "string" },
          ],
        },
      }),

      // 5. AI season-specific pitches
      aiNode(
        "5",
        "AI Season-Specific Pitch Email",
        1000,
        "You are a retail sales specialist. Given the seasonal trends, prospect vertical, and whether they are a customer or prospect, craft a season-specific pitch email. For customers, focus on upselling seasonal features and templates. For warm prospects, lead with seasonal urgency and value. For cold prospects, lead with industry trends and pain points. Respond JSON: {seasonal_subject: string, seasonal_body_html: string, seasonal_cta: string, offer_positioning: string, follow_up_angle: string}",
        "Season: {season_name}. Theme: {promotion_theme}. Offer: {offer_details}. Prospect: {prospect_first} {prospect_last} at {prospect_company} ({retail_vertical}). Is customer: {is_existing_customer}. Pitch intensity: {pitch_intensity}. Relevance: {seasonal_relevance}. Trend 1: {seasonal_trend_1}. Trend 2: {seasonal_trend_2}. Challenge: {marketing_challenge}. Solution angle: {our_solution_angle}. Urgency: {urgency_factor}. Craft the seasonal pitch.",
        [
          { field: "seasonal_subject", as: "seasonal_subject", type: "string" },
          {
            field: "seasonal_body_html",
            as: "seasonal_body_html",
            type: "string",
          },
          { field: "seasonal_cta", as: "seasonal_cta", type: "string" },
          {
            field: "offer_positioning",
            as: "offer_positioning",
            type: "string",
          },
          { field: "follow_up_angle", as: "follow_up_angle", type: "string" },
        ],
      ),

      // 6. Branch by customer vs prospect
      n("6", "condition", "Existing Customer?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "is_existing_customer", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Validate email addresses via ZeroBounce
      n("7", "hitApi", "Validate Email Addresses", 1480, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zerobounce.net/v2/validate?api_key={zerobounce_api_key}&email={prospect_email}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "status",
              customName: "email_validation_status",
              type: "string",
            },
            {
              name: "sub_status",
              customName: "email_sub_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Queue timed email delivery with delay
      n("8", "delayAction", "Queue Timed Email Delivery", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 2,
          delayUnit: "hours",
          description:
            "Delay email delivery by 2 hours to optimize for morning inbox arrival times",
        },
      }),

      // 9. Track campaign performance in Sheets
      sheetsNode(
        "9",
        "Track Campaign Performance Sheets",
        1960,
        "SeasonalCampaign",
        '[["{season_name}","{prospect_first} {prospect_last}","{prospect_email}","{prospect_company}","{retail_vertical}","{is_existing_customer}","{pitch_intensity}","{seasonal_relevance}","{seasonal_subject}","{email_validation_status}","sent","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "7", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1886. REVIEW CHAMPION CAMPAIGN BUILDER (9 nodes)
  // ===================================================================

  "Review Champion Campaign Builder": {
    summary:
      "Runs on a schedule to identify happy customers from HubSpot, scores review propensity with runCode, drafts platform-specific review asks with AI, branches by customer tier, sends review request emails, monitors review submissions in Sheets, sends thank-you reward emails, and reports campaign results in Sheets.",
    nodes: [
      trigger("Schedule: Weekly review campaign", "schedule", {
        schedule: "0 10 * * 2",
        howItStarts:
          "Runs weekly on Tuesday mornings to identify satisfied customers and launch targeted review requests across G2, Capterra, TrustRadius, and other review platforms — maximizes review volume through personalized, tier-appropriate asks",
        setup:
          "Configure HubSpot API token for customer identification. Set OpenAI API key for review ask drafting. Configure email sending credentials. Set Google Sheets credentials for submission monitoring and campaign reporting.",
        inputFields: [],
      }),

      // 2. Identify happy customers from HubSpot
      hubspotGetNode(
        "2",
        "Identify Happy Customers HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          { name: "results[0].id", customName: "customer_id", type: "string" },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "customer_first",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "customer_last",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "customer_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "customer_title",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "page_views",
            type: "string",
          },
          {
            name: "results[0].properties.notes_last_contacted",
            customName: "last_contacted",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle",
            type: "string",
          },
        ],
      ),

      // 3. Score review propensity runCode
      n("3", "runCode", "Score Review Propensity", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score likelihood of leaving a review based on engagement signals
const pageViews = parseInt(vars.page_views) || 0;
const title = (vars.customer_title || "").toLowerCase();
const lastContacted = vars.last_contacted ? new Date(vars.last_contacted) : null;
const daysSinceContact = lastContacted ? Math.floor((Date.now() - lastContacted.getTime()) / (1000 * 60 * 60 * 24)) : 999;
let propensityScore = 0;
if (pageViews > 50) propensityScore += 25;
else if (pageViews > 20) propensityScore += 15;
else propensityScore += 5;
if (daysSinceContact < 14) propensityScore += 30;
else if (daysSinceContact < 30) propensityScore += 20;
else if (daysSinceContact < 60) propensityScore += 10;
if (title.includes("manager") || title.includes("director") || title.includes("vp") || title.includes("head")) propensityScore += 20;
else propensityScore += 10;
propensityScore += Math.min(15, Math.floor(pageViews / 10));
const customerTier = propensityScore >= 70 ? "champion" : propensityScore >= 45 ? "advocate" : "supporter";
const targetPlatform = customerTier === "champion" ? "G2 + Capterra + TrustRadius" : customerTier === "advocate" ? "G2 + Capterra" : "G2";
const incentiveLevel = customerTier === "champion" ? "premium_gift_card" : customerTier === "advocate" ? "standard_gift_card" : "digital_swag";
return {
  propensity_score: propensityScore,
  customer_tier: customerTier,
  target_platform: targetPlatform,
  incentive_level: incentiveLevel,
  days_since_contact: daysSinceContact,
  should_request: propensityScore >= 30 ? "true" : "false"
};`,
          outputVariables: [
            { name: "propensity_score", type: "number" },
            { name: "customer_tier", type: "string" },
            { name: "target_platform", type: "string" },
            { name: "incentive_level", type: "string" },
            { name: "days_since_contact", type: "number" },
            { name: "should_request", type: "string" },
          ],
        },
      }),

      // 4. AI platform-specific review asks
      aiNode(
        "4",
        "AI Draft Platform-Specific Review Ask",
        760,
        "You are a customer advocacy specialist who excels at getting authentic reviews. Given the customer profile, their tier, and target review platform(s), craft a personalized review request email. For champions, emphasize their expert status and impact. For advocates, highlight community contribution. For supporters, keep it simple and quick. Always mention the specific platform and incentive. Respond JSON: {review_subject: string, review_body_html: string, platform_links: string, incentive_description: string, time_estimate: string}",
        "Customer: {customer_first} {customer_last} at {customer_company} ({customer_title}). Tier: {customer_tier} (score {propensity_score}). Platform(s): {target_platform}. Incentive: {incentive_level}. Days since contact: {days_since_contact}. Page views: {page_views}. Craft the review request.",
        [
          { field: "review_subject", as: "review_subject", type: "string" },
          { field: "review_body_html", as: "review_body_html", type: "string" },
          { field: "platform_links", as: "platform_links", type: "string" },
          {
            field: "incentive_description",
            as: "incentive_description",
            type: "string",
          },
          { field: "time_estimate", as: "time_estimate", type: "string" },
        ],
      ),

      // 5. Branch by customer tier
      n("5", "condition", "Champion or Advocate Tier?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "customer_tier",
              operator: "in",
              value: "champion,advocate",
            },
          ],
        },
      }),

      // 6. Send review request email
      emailNode(
        "6",
        "Send Review Request Email",
        1240,
        "{customer_email}",
        "{review_subject}",
        "{review_body_html}<p>Review here: {platform_links}</p><p>Estimated time: {time_estimate}</p><p>Your reward: {incentive_description}</p>",
      ),

      // 7. Monitor review submissions in Sheets
      sheetsNode(
        "7",
        "Monitor Review Submissions Sheets",
        1480,
        "ReviewRequests",
        '[["{customer_first} {customer_last}","{customer_email}","{customer_company}","{customer_tier}","{propensity_score}","{target_platform}","{incentive_level}","requested","pending","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),

      // 8. Send thank-you reward email (champion/advocate path)
      emailNode(
        "8",
        "Send Thank-You Reward Email",
        1240,
        "{customer_email}",
        "Thank you for your review, {customer_first}!",
        "<p>Hi {customer_first},</p><p>Thank you for taking the time to share your experience on {target_platform}. Your review helps other professionals discover how {customer_company} uses our platform.</p><p>As promised, here is your reward: {incentive_description}</p><p>We truly appreciate your support as a {customer_tier}!</p>",
        400,
      ),

      // 9. Report campaign results in Sheets
      sheetsNode(
        "9",
        "Report Campaign Results Sheets",
        1720,
        "ReviewCampaignResults",
        '[["{customer_tier}","{target_platform}","{propensity_score}","{incentive_level}","email_sent","{customer_company}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "9"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1887. REVIEW SITE ALERT TO SALES (8 nodes)
  // ===================================================================

  "Review Site Alert to Sales": {
    summary:
      "Runs on a schedule to fetch new competitor reviews via API, analyzes displacement signals with AI, enriches reviewer identity with runCode, scores displacement opportunity with runCode, branches by opportunity score, alerts sales with a battlecard via Slack, and creates a target account in HubSpot CRM.",
    nodes: [
      trigger("Schedule: Competitor review scan", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs daily to scan G2, Capterra, and TrustRadius for new competitor reviews — identifies reviewers who express dissatisfaction, compare alternatives, or signal switching intent, creating targeted sales opportunities",
        setup:
          "Configure G2 or review platform API credentials for review fetching. Set OpenAI API key for displacement signal analysis. Set HubSpot API token for target account creation. Configure Slack webhook for sales alerts.",
        inputFields: [],
      }),

      // 2. Fetch new competitor reviews via API
      n("2", "hitApi", "Fetch New Competitor Reviews API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://data.g2.com/api/v1/survey-responses?filter[product_name]=COMPETITOR_NAME&filter[created_at_gte]={yesterday_date}&page[size]=25",
          headers: [
            { key: "Authorization", value: "Token token={g2_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].attributes.title",
              customName: "review_title",
              type: "string",
            },
            {
              name: "data[0].attributes.comment_answers.love",
              customName: "review_likes",
              type: "string",
            },
            {
              name: "data[0].attributes.comment_answers.hate",
              customName: "review_dislikes",
              type: "string",
            },
            {
              name: "data[0].attributes.star_rating",
              customName: "star_rating",
              type: "number",
            },
            {
              name: "data[0].attributes.submitted_at",
              customName: "review_date",
              type: "string",
            },
            {
              name: "data[0].attributes.company_name",
              customName: "reviewer_company",
              type: "string",
            },
            {
              name: "data[0].attributes.company_size",
              customName: "reviewer_company_size",
              type: "string",
            },
            {
              name: "data[0].attributes.industry",
              customName: "reviewer_industry",
              type: "string",
            },
            {
              name: "data[0].attributes.user_role",
              customName: "reviewer_role",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI analyze displacement signals
      aiNode(
        "3",
        "AI Analyze Displacement Signals",
        520,
        "You are a competitive sales intelligence analyst. Given a competitor review from G2 or similar platform, analyze the content for displacement signals. Look for expressions of frustration, comparison mentions, switching considerations, unmet needs, and feature gaps. Assess how well our product addresses the reviewer concerns. Respond JSON: {displacement_signal_strength: string, key_frustrations: string, unmet_needs: string, our_advantage_match: string, switching_likelihood: string, recommended_pitch_angle: string, battlecard_points: string}",
        "Review of competitor by {reviewer_role} at {reviewer_company} ({reviewer_industry}, {reviewer_company_size}). Rating: {star_rating}/5. Title: {review_title}. Likes: {review_likes}. Dislikes: {review_dislikes}. Analyze displacement signals.",
        [
          {
            field: "displacement_signal_strength",
            as: "signal_strength",
            type: "string",
          },
          { field: "key_frustrations", as: "key_frustrations", type: "string" },
          { field: "unmet_needs", as: "unmet_needs", type: "string" },
          {
            field: "our_advantage_match",
            as: "our_advantage_match",
            type: "string",
          },
          {
            field: "switching_likelihood",
            as: "switching_likelihood",
            type: "string",
          },
          {
            field: "recommended_pitch_angle",
            as: "recommended_pitch_angle",
            type: "string",
          },
          {
            field: "battlecard_points",
            as: "battlecard_points",
            type: "string",
          },
        ],
      ),

      // 4. Enrich reviewer identity runCode
      n("4", "runCode", "Enrich Reviewer Identity", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Enrich and analyze reviewer profile for sales targeting
const company = vars.reviewer_company || "";
const companySize = (vars.reviewer_company_size || "").toLowerCase();
const industry = vars.reviewer_industry || "";
const role = (vars.reviewer_role || "").toLowerCase();
const rating = parseInt(vars.star_rating) || 3;
const isDecisionMaker = role.includes("manager") || role.includes("director") || role.includes("vp") || role.includes("chief") || role.includes("head");
const isEndUser = role.includes("user") || role.includes("analyst") || role.includes("specialist") || role.includes("coordinator");
let companyTier = "smb";
if (companySize.includes("enterprise") || companySize.includes("1000") || companySize.includes("5000") || companySize.includes("10000")) companyTier = "enterprise";
else if (companySize.includes("mid") || companySize.includes("201") || companySize.includes("500")) companyTier = "mid_market";
const estimatedDomain = company.toLowerCase().replace(/[^a-z0-9]/g, "") + ".com";
return {
  is_decision_maker: isDecisionMaker ? "true" : "false",
  reviewer_persona: isDecisionMaker ? "decision_maker" : isEndUser ? "end_user" : "influencer",
  company_tier: companyTier,
  estimated_domain: estimatedDomain,
  dissatisfaction_level: rating <= 2 ? "high" : rating <= 3 ? "moderate" : "low",
  target_priority: isDecisionMaker && rating <= 3 ? "high" : rating <= 2 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "is_decision_maker", type: "string" },
            { name: "reviewer_persona", type: "string" },
            { name: "company_tier", type: "string" },
            { name: "estimated_domain", type: "string" },
            { name: "dissatisfaction_level", type: "string" },
            { name: "target_priority", type: "string" },
          ],
        },
      }),

      // 5. Score displacement opportunity runCode
      n("5", "runCode", "Score Displacement Opportunity", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate final opportunity score for sales prioritization
const signalStrength = (vars.signal_strength || "").toLowerCase();
const switchingLikelihood = (vars.switching_likelihood || "").toLowerCase();
const dissatisfaction = (vars.dissatisfaction_level || "").toLowerCase();
const companyTier = (vars.company_tier || "").toLowerCase();
const isDecisionMaker = vars.is_decision_maker === "true";
let oppScore = 0;
if (signalStrength === "strong") oppScore += 30;
else if (signalStrength === "moderate") oppScore += 18;
else oppScore += 5;
if (switchingLikelihood.includes("high") || switchingLikelihood.includes("likely")) oppScore += 25;
else if (switchingLikelihood.includes("moderate") || switchingLikelihood.includes("possible")) oppScore += 15;
else oppScore += 5;
if (dissatisfaction === "high") oppScore += 20;
else if (dissatisfaction === "moderate") oppScore += 10;
if (companyTier === "enterprise") oppScore += 15;
else if (companyTier === "mid_market") oppScore += 10;
if (isDecisionMaker) oppScore += 10;
const oppTier = oppScore >= 70 ? "hot_target" : oppScore >= 45 ? "warm_target" : "monitor";
const shouldAlert = oppScore >= 40 ? "true" : "false";
return {
  opportunity_score: oppScore,
  opportunity_tier: oppTier,
  should_alert_sales: shouldAlert,
  sales_action: oppTier === "hot_target" ? "immediate_outreach" : oppTier === "warm_target" ? "add_to_sequence" : "track_in_crm"
};`,
          outputVariables: [
            { name: "opportunity_score", type: "number" },
            { name: "opportunity_tier", type: "string" },
            { name: "should_alert_sales", type: "string" },
            { name: "sales_action", type: "string" },
          ],
        },
      }),

      // 6. Branch by opportunity score
      n("6", "condition", "High Displacement Opportunity?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "should_alert_sales", operator: "eq", value: "true" },
          ],
        },
      }),

      // 7. Alert sales with battlecard via Slack
      slackNode(
        "7",
        "Alert Sales with Battlecard Slack",
        1480,
        'Displacement Alert [{opportunity_tier}]: *{reviewer_company}* ({company_tier})\\nReview: {star_rating}/5 — "{review_title}"\\nRole: {reviewer_role} ({reviewer_persona}) | Decision maker: {is_decision_maker}\\nIndustry: {reviewer_industry} | Score: {opportunity_score}\\n\\nKey frustrations: {key_frustrations}\\nUnmet needs: {unmet_needs}\\nOur advantage: {our_advantage_match}\\nSwitching likelihood: {switching_likelihood}\\n\\nBattlecard:\\n{battlecard_points}\\n\\nPitch angle: {recommended_pitch_angle}\\nAction: {sales_action}',
      ),

      // 8. Create target account in CRM HubSpot
      hubspotPostNode(
        "8",
        "Create Target Account CRM HubSpot",
        1720,
        "crm/v3/objects/companies",
        [
          {
            key: "properties",
            value:
              '{"name":"{reviewer_company}","domain":"{estimated_domain}","industry":"{reviewer_industry}","numberofemployees":"{reviewer_company_size}","hs_lead_status":"OPEN","description":"Competitor displacement target. Review score: {star_rating}/5. Opportunity: {opportunity_tier} ({opportunity_score}). Frustrations: {key_frustrations}. Pitch angle: {recommended_pitch_angle}. Battlecard: {battlecard_points}"}',
          },
        ],
        [
          { name: "id", customName: "created_company_id", type: "string" },
          {
            name: "properties.name",
            customName: "created_company_name",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },
};
